import { useState, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import { pb } from "./pb";
import { PK, GOALS, daysUntilBirthday } from "./constants";
import { effectiveDeadline, fetchAllDayVacations, daysUntil as pkgDaysUntil } from "./packageDeadline";
import WorkoutPlanBuilder, { ExerciseSummary } from "./WorkoutPlanBuilder";
import BookingAdmin, { downloadIcal } from "./BookingAdmin";
import TrainerStats from "./TrainerStats";
import ProgressCompare from "./ProgressCompare";
import PushPermissionPrompt from "./PushNotifications";
import StreakBadge from "./StreakBadge";
import WorkoutPresets from "./WorkoutPresets";
import PackageAdmin from "./PackageAdmin";
import ClientMeasurements from "./ClientMeasurements";
import LiveTraining from "./LiveTraining";
import ClientInfo from "./ClientInfo";
import ClientReport from "./ClientReport";
import { SearchInput, ShowMoreButton } from "./ui/kit";
import { useIsDesktop, useIsWide } from "./ui/responsive";
import { attemptExit } from "./ui/exitGuard";
import { subscribeLiveSession, getActiveLiveSession, setActiveLiveSession } from "./ui/liveSessionStore";
import { Clipboard, Footprints, Moon, Droplet, CheckCircle, Heart, AlertTriangle, Ban, Dot, Ruler, Camera, Dumbbell, ChevronLeft, ChevronRight, Users, Calendar, Ticket, BarChart, Cake, Refresh, Glass, Flame, Muscle, Salad, Timer } from "./ui/icons";
import { resolveMacroTargets } from "./macroCalc";

const BDAY_KEYFRAMES = `
@keyframes bdayCardGlow { 0%, 100% { box-shadow: 0 0 0 1px rgba(255,215,0,0.35), 0 0 14px rgba(255,215,0,0.15); } 50% { box-shadow: 0 0 0 1px rgba(255,215,0,0.6), 0 0 22px rgba(255,215,0,0.35); } }
@keyframes bdayBadgeBounce { 0%, 100% { transform: scale(1) rotate(-4deg); } 50% { transform: scale(1.15) rotate(4deg); } }
@keyframes livePulse { 0%, 100% { transform: scale(1); opacity: 1; } 50% { transform: scale(1.4); opacity: .5; } }
`;

const TRAFFIC_LABEL = { 1: "Blogai", 2: "Vidutiniškai", 3: "Gerai" };
const TRAFFIC_COLOR = { 1: ["#FF7A6E", "#E14A45"], 2: ["#FFC15E", "#F2A63D"], 3: ["#5CE3A6", "#2FBE84"] };

// Nuolat matomas (visose skiltyse, per portal tiesiai į document.body)
// indikatorius, kai kažkuriam klientui yra pradėta, bet dar nebaigta gyva
// treniruotė — kad trenerė galėtų bet kada vienu paspaudimu grįžti į ją,
// neieškodama kliento iš naujo. Jo būsena ateina iš ui/liveSessionStore.js,
// kurią atnaujina LiveTraining.js.
function LiveSessionBanner({ info, onClick }) {
  const isDesktop = useIsDesktop();
  if (!info) return null;
  return createPortal(
    <button onClick={onClick} style={{
      position: "fixed", left: "50%", transform: "translateX(-50%)",
      // Mobiliuosiuose reikia pakilti virš apatinės tabų juostos (ji visada
      // rodoma, ~84px aukščio su savo saugiu kraštu); kompiuteryje/iPade
      // apatinės juostos nėra, tad užtenka mažo atstumo nuo krašto.
      bottom: isDesktop ? "max(16px, calc(env(safe-area-inset-bottom) + 10px))" : "max(84px, calc(env(safe-area-inset-bottom) + 70px))",
      zIndex: 1250, display: "flex", alignItems: "center", gap: 9,
      background: "linear-gradient(135deg,#AD1457,#E91E8C)", border: "none", borderRadius: 30,
      padding: "11px 16px 11px 14px", color: "#fff", cursor: "pointer", fontFamily: "inherit",
      boxShadow: "0 8px 24px rgba(173,20,87,0.5)", maxWidth: "calc(100vw - 32px)",
    }}>
      <Dot color="#fff" size={8} style={{ animation: "livePulse 1.2s ease-in-out infinite", flexShrink: 0 }} />
      <span style={{ fontSize: 13, fontWeight: 700, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
        Treniruotė vyksta · {info.clientName}
      </span>
      <ChevronRight size={14} style={{ flexShrink: 0 }} />
    </button>,
    document.body
  );
}

function ProgressRing({ pct, c1, c2, hit, size = 40 }) {
  const r = (size - 6) / 2, cx = size / 2, cy = size / 2, C = 2 * Math.PI * r;
  const dash = (Math.min(100, Math.max(0, pct)) / 100) * C;
  return (
    <div style={{ position:"relative", width:size, height:size, flexShrink:0 }}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} style={{ transform:"rotate(-90deg)" }}>
        <circle cx={cx} cy={cy} r={r} fill="none" stroke="rgba(255,255,255,0.14)" strokeWidth="4" />
        <circle cx={cx} cy={cy} r={r} fill="none" stroke={c1} strokeWidth="4" strokeLinecap="round"
          strokeDasharray={C} strokeDashoffset={C - dash}
          style={{ transition:"stroke-dashoffset 0.7s cubic-bezier(.23,1,.32,1)", filter: hit ? `drop-shadow(0 0 4px ${c1}99)` : "none" }} />
      </svg>
      <div style={{ position:"absolute", inset:0, display:"flex", alignItems:"center", justifyContent:"center", fontSize:9, fontWeight:800, color:c1 }}>
        {Math.round(pct)}%
      </div>
    </div>
  );
}

function DashStatCard({ icon: Icon, value, label, c1, c2, pct }) {
  const hit = pct != null && pct >= 100;
  return (
    <div style={{
      position:"relative", overflow:"hidden", borderRadius:16, padding:"14px 12px",
      background:`linear-gradient(145deg, ${c1}26, ${c2}12)`,
      border:`1px solid ${c1}3d`,
      display:"flex", alignItems:"center", gap:10,
    }}>
      <div style={{
        position:"absolute", top:-26, right:-26, width:70, height:70, borderRadius:"50%",
        background:`radial-gradient(circle, ${c1}3d, transparent 70%)`,
      }} />
      <div style={{
        position:"relative", width:32, height:32, borderRadius:"50%", flexShrink:0,
        background:`linear-gradient(135deg, ${c1}33, ${c2}22)`,
        display:"flex", alignItems:"center", justifyContent:"center",
        boxShadow: hit ? `0 0 0 3px ${c1}33` : "none",
      }}>
        <Icon size={16} color={c1} />
      </div>
      <div style={{ position:"relative", flex:1, minWidth:0 }}>
        <div style={{ fontSize:20, fontWeight:800, color:"#fff" }}>{value}</div>
        <div style={{ fontSize:10, color:"rgba(255,255,255,0.55)", lineHeight:1.25 }}>{label}</div>
      </div>
      {pct != null && <ProgressRing pct={pct} c1={c1} c2={c2} hit={hit} />}
    </div>
  );
}

function TrafficOrb({ v, size = 28 }) {
  const c = TRAFFIC_COLOR[v];
  return (
    <div style={{
      width: size, height: size, borderRadius: "50%", margin: "0 auto",
      background: c ? `radial-gradient(circle at 34% 28%, ${c[0]}, ${c[1]})` : "rgba(255,255,255,0.1)",
      boxShadow: c ? `0 2px 8px ${c[0]}55` : "none",
    }} />
  );
}

function todayStr() { return new Date().toISOString().split("T")[0]; }
function daysAgoStr(n) { const d = new Date(); d.setDate(d.getDate() - n); return d.toISOString().split("T")[0]; }

async function adminCreateUser(email, password) {
  const data = await pb.collection("users").create({
    email, password, passwordConfirm: password,
    role: "client", onboarding_done: false, emailVisibility: true,
  });
  if (!data?.id) throw new Error("Klaida kuriant vartotoją");
  return data;
}

// ── Mini kalendorius ──────────────────────────────────────────────────────────
function MiniCalendar({ selectedDate, onSelect, checkinDates }) {
  const [month, setMonth] = useState(() => {
    const d = new Date(selectedDate + "T12:00:00");
    return { y: d.getFullYear(), m: d.getMonth() };
  });
  const today = todayStr();
  const firstDay = new Date(month.y, month.m, 1).getDay();
  const daysInMonth = new Date(month.y, month.m + 1, 0).getDate();
  const offset = firstDay === 0 ? 6 : firstDay - 1;

  return (
    <div style={{ background: "rgba(255,255,255,0.08)", borderRadius: 18, padding: "16px", marginBottom: 16, border: "1px solid rgba(255,255,255,0.15)" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
        <button onClick={() => setMonth(p => { const d = new Date(p.y, p.m - 1, 1); return { y: d.getFullYear(), m: d.getMonth() }; })}
          style={{ background: "rgba(255,255,255,0.1)", border: "none", borderRadius: 8, padding: "6px 12px", color: "#fff", cursor: "pointer", fontSize: 14 }}>◀</button>
        <span style={{ color: "#fff", fontWeight: 700, fontSize: 14 }}>
          {new Date(month.y, month.m, 1).toLocaleDateString("lt-LT", { month: "long", year: "numeric" })}
        </span>
        <button onClick={() => setMonth(p => { const d = new Date(p.y, p.m + 1, 1); return { y: d.getFullYear(), m: d.getMonth() }; })}
          style={{ background: "rgba(255,255,255,0.1)", border: "none", borderRadius: 8, padding: "6px 12px", color: "#fff", cursor: "pointer", fontSize: 14 }}>▶</button>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(7,1fr)", gap: 3, marginBottom: 6 }}>
        {["P","A","T","K","P","Š","S"].map(d => (
          <div key={d} style={{ textAlign: "center", fontSize: 9, color: "rgba(255,255,255,0.35)", padding: "2px 0" }}>{d}</div>
        ))}
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(7,1fr)", gap: 3 }}>
        {Array(offset).fill(null).map((_,i) => <div key={"e"+i} />)}
        {Array(daysInMonth).fill(null).map((_,i) => {
          const d = i + 1;
          const dateStr = `${month.y}-${String(month.m+1).padStart(2,"0")}-${String(d).padStart(2,"0")}`;
          const isSelected = dateStr === selectedDate;
          const isToday = dateStr === today;
          const hasCheckin = checkinDates?.includes(dateStr);
          const isFuture = dateStr > today;
          return (
            <button key={d} onClick={() => !isFuture && onSelect(dateStr)}
              style={{
                aspectRatio: "1", borderRadius: 8, border: isSelected ? "2px solid rgba(255,255,255,0.9)" : "1px solid transparent",
                background: isSelected ? "rgba(255,255,255,0.25)" : isToday ? "rgba(255,255,255,0.12)" : "transparent",
                cursor: isFuture ? "default" : "pointer",
                color: isFuture ? "rgba(255,255,255,0.15)" : "#fff",
                fontSize: 12, fontWeight: isSelected || isToday ? 700 : 400, fontFamily: "inherit",
                position: "relative",
              }}>
              {d}
              {hasCheckin && !isSelected && (
                <div style={{ position: "absolute", bottom: 2, left: "50%", transform: "translateX(-50%)", width: 4, height: 4, borderRadius: "50%", background: "#7FFFB0" }} />
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ── Dienos duomenų vaizdas (check-in / miegas / vanduo / kcal ir makro) ──────
function DayView({ client, date }) {
  const clientId = client.id;
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [targets, setTargets] = useState(null); // kliento kcal/makro tikslai — tie patys visoms dienoms

  useEffect(() => {
    async function load() {
      setLoading(true);
      const [checkin, sleep, water] = await Promise.all([
        pb.collection("daily_checkins").getList(1, 1, { filter: `user_id="${clientId}" && date="${date}"`, requestKey: null })
          .then(r => r.items[0] || null).catch(() => null),
        pb.collection("sleep_log").getList(1, 1, { filter: `user_id="${clientId}" && date="${date}"`, requestKey: null })
          .then(r => r.items[0] || null).catch(() => null),
        pb.collection("water_log").getList(1, 1, { filter: `user_id="${clientId}" && date="${date}"`, requestKey: null })
          .then(r => r.items[0] || null).catch(() => null),
      ]);
      setData({ checkin, sleep, water });
      setLoading(false);
    }
    load();
  }, [clientId, date]);

  useEffect(() => {
    resolveMacroTargets(clientId, client).then(setTargets);
    // eslint-disable-next-line
  }, [clientId]);

  const fmtDate = (d) => new Date(d + "T12:00:00").toLocaleDateString("lt-LT", { weekday: "long", month: "long", day: "numeric" });

  if (loading) return <p style={{ color: "rgba(255,255,255,0.4)", fontSize: 13, textAlign: "center", padding: "20px 0" }}>Kraunama...</p>;

  const { checkin, sleep, water } = data;
  const hasMacros = checkin?.protein_g || checkin?.fat_g || checkin?.carbs_g;
  const hasAny = checkin || sleep || water;
  const kcal = hasMacros ? (checkin.protein_g||0)*4 + (checkin.fat_g||0)*9 + (checkin.carbs_g||0)*4 : null;
  const kcalTarget = targets?.targets?.target;

  return (
    <div style={{ background: "rgba(0,0,0,0.2)", borderRadius: 16, padding: "16px" }}>
      <p style={{ fontSize: 11, fontWeight: 700, color: "rgba(255,255,255,0.5)", textTransform: "uppercase", margin: "0 0 12px" }}>
        {fmtDate(date)}
      </p>

      {!hasAny && (
        <p style={{ color: "rgba(255,255,255,0.3)", fontSize: 13, textAlign: "center", padding: "12px 0" }}>Šiai dienai duomenų nėra</p>
      )}

      {checkin && (
        <div style={{ marginBottom: 12 }}>
          <p style={{ fontSize: 11, color: "rgba(255,255,255,0.5)", margin: "0 0 8px", fontWeight: 600, display:"flex", alignItems:"center", gap:5 }}><Clipboard size={12} />Check-in</p>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8 }}>
            <div style={{ background: "rgba(255,255,255,0.08)", borderRadius: 12, padding: "12px", textAlign: "center" }}>
              <div style={{ marginBottom: 8 }}><TrafficOrb v={checkin.nutrition_score} /></div>
              <div style={{ fontSize: 11, color: "#fff", fontWeight: 600 }}>{TRAFFIC_LABEL[checkin.nutrition_score] || "–"}</div>
              <div style={{ fontSize: 10, color: "rgba(255,255,255,0.4)", marginTop: 2 }}>Mityba</div>
            </div>
            <div style={{ background: "rgba(255,255,255,0.08)", borderRadius: 12, padding: "12px", textAlign: "center" }}>
              <div style={{ marginBottom: 8 }}><TrafficOrb v={checkin.wellbeing_score} /></div>
              <div style={{ fontSize: 11, color: "#fff", fontWeight: 600 }}>{TRAFFIC_LABEL[checkin.wellbeing_score] || "–"}</div>
              <div style={{ fontSize: 10, color: "rgba(255,255,255,0.4)", marginTop: 2 }}>Savijauta</div>
            </div>
            <div style={{ background: "rgba(255,255,255,0.08)", borderRadius: 12, padding: "12px", textAlign: "center" }}>
              <div style={{ marginBottom: 8, display:"flex", justifyContent:"center" }}><Glass size={26} color={checkin.alcohol == null ? "rgba(255,255,255,0.2)" : checkin.alcohol ? "#FF7A6E" : "#5CE3A6"} /></div>
              <div style={{ fontSize: 11, color: "#fff", fontWeight: 600 }}>{checkin.alcohol == null ? "–" : checkin.alcohol ? "Taip" : "Ne"}</div>
              <div style={{ fontSize: 10, color: "rgba(255,255,255,0.4)", marginTop: 2 }}>Alkoholis</div>
            </div>
          </div>
        </div>
      )}

      {checkin?.steps > 0 && (
        <div style={{ background: "rgba(255,255,255,0.08)", borderRadius: 12, padding: "12px", marginBottom: 8, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <span style={{ fontSize: 13, color: "rgba(255,255,255,0.7)", display:"inline-flex", alignItems:"center", gap:5 }}><Footprints size={13} />Žingsniai</span>
          <span style={{ fontSize: 14, fontWeight: 700, color: checkin.steps >= 7000 ? "#7FFFB0" : "#fff" }}>{checkin.steps.toLocaleString()}</span>
        </div>
      )}

      {sleep && (
        <div style={{ background: "rgba(255,255,255,0.08)", borderRadius: 12, padding: "12px", marginBottom: 8, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <span style={{ fontSize: 13, color: "rgba(255,255,255,0.7)", display:"inline-flex", alignItems:"center", gap:5 }}><Moon size={13} />Miegas</span>
          <span style={{ fontSize: 14, fontWeight: 700, color: "#fff" }}>{sleep.hours_slept}h</span>
        </div>
      )}

      {water && (
        <div style={{ background: "rgba(255,255,255,0.08)", borderRadius: 12, padding: "12px", marginBottom: hasMacros ? 8 : 0, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <span style={{ fontSize: 13, color: "rgba(255,255,255,0.7)", display:"inline-flex", alignItems:"center", gap:5 }}><Droplet size={13} />Vanduo</span>
          <span style={{ fontSize: 14, fontWeight: 700, color: "#fff" }}>{water.ml} / {water.goal} ml</span>
        </div>
      )}

      {hasMacros && (() => {
        const rows = [
          { label:"Baltymai", Icon:Muscle,  color:"#FF6EB4", v:checkin.protein_g, t:targets?.targets?.prot.g },
          { label:"Riebalai",  Icon:Droplet, color:"#FFD700", v:checkin.fat_g,     t:targets?.targets?.fat.g  },
          { label:"Angliav.",  Icon:Salad,   color:"#7FFFB0", v:checkin.carbs_g,   t:targets?.targets?.carb.g },
        ];
        return (
          <div style={{ background: "rgba(255,255,255,0.08)", borderRadius: 12, padding: "12px" }}>
            <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom: 8 }}>
              <span style={{ fontSize: 13, color: "rgba(255,255,255,0.7)", display:"inline-flex", alignItems:"center", gap:5 }}><Flame size={13} color="#FFA500" />Kalorijos</span>
              <span style={{ fontSize: 14, fontWeight: 700, color: "#fff" }}>{Math.round(kcal)}{kcalTarget ? ` / ${kcalTarget} kcal` : " kcal"}</span>
            </div>
            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:8 }}>
              {rows.map(r => (
                <div key={r.label} style={{ background:"rgba(0,0,0,0.18)", borderRadius:10, padding:"8px 6px", textAlign:"center" }}>
                  <r.Icon size={13} color={r.color} style={{ marginBottom:4 }} />
                  <p style={{ fontSize:12, fontWeight:700, color:"#fff", margin:0 }}>{r.v||0}{r.t ? <span style={{ fontWeight:400, color:"rgba(255,255,255,0.4)" }}>/{r.t}</span> : null}g</p>
                  <p style={{ fontSize:9, color:"rgba(255,255,255,0.4)", margin:0 }}>{r.label}</p>
                </div>
              ))}
            </div>
          </div>
        );
      })()}

      {/* Trumpa išvada */}
      {hasAny && (
        <div style={{ marginTop: 12, padding: "10px 12px", background: "rgba(255,255,255,0.06)", borderRadius: 10, borderLeft: "3px solid rgba(255,255,255,0.3)" }}>
          <p style={{ fontSize: 11, color: "rgba(255,255,255,0.6)", margin: 0, display:"flex", flexWrap:"wrap", alignItems:"center", gap:4 }}>
            {(() => {
              const parts = [];
              if (checkin?.nutrition_score === 3) parts.push({ text:"Mityba gerai", Icon:CheckCircle });
              else if (checkin?.nutrition_score === 1) parts.push({ text:"Mityba prasta, reikia aptarti", dot:"#FF7A6E" });
              else if (checkin?.nutrition_score === 2) parts.push({ text:"Mityba vidutiniškai", dot:"#FFC15E" });
              if (checkin?.wellbeing_score === 3) parts.push({ text:"Savijauta puiki", Icon:Heart, color:"#7FFFB0" });
              else if (checkin?.wellbeing_score === 1) parts.push({ text:"Savijauta bloga", dot:"#FF7A6E" });
              else if (checkin?.wellbeing_score === 2) parts.push({ text:"Savijauta vidutinė", dot:"#FFC15E" });
              if (checkin?.alcohol === true) parts.push({ text:"Vartojo alkoholį", dot:"#FF7A6E" });
              else if (checkin?.alcohol === false) parts.push({ text:"Alkoholio nevartojo", Icon:CheckCircle });
              if (checkin?.steps >= 7000) parts.push({ text:`Žingsnių tikslas pasiektas (${checkin.steps.toLocaleString()})` });
              else if (checkin?.steps > 0) parts.push({ text:`Žingsniai: ${checkin.steps.toLocaleString()}` });
              if (sleep?.hours_slept >= 7) parts.push({ text:`Miegas pakankamas (${sleep.hours_slept}h)` });
              else if (sleep?.hours_slept) parts.push({ text:`Miegas per mažas (${sleep.hours_slept}h)`, Icon:AlertTriangle });
              if (water?.ml >= (water?.goal || 2000)) parts.push({ text:"Vanduo išgertas", Icon:CheckCircle });
              else if (water?.ml) parts.push({ text:`Vanduo: ${(water.ml/1000).toFixed(1)}L iš ${((water.goal||2000)/1000).toFixed(1)}L` });
              if (kcal != null) {
                if (kcalTarget && kcal >= kcalTarget*0.85 && kcal <= kcalTarget*1.15) parts.push({ text:`Kalorijos arti tikslo (${Math.round(kcal)}/${kcalTarget})`, Icon:CheckCircle });
                else parts.push({ text:`Kalorijos: ${Math.round(kcal)}${kcalTarget?`/${kcalTarget}`:""} kcal` });
              }
              if (!parts.length) return "Duomenys surinkti.";
              return parts.map((p, i) => (
                <span key={i} style={{ display:"inline-flex", alignItems:"center", gap:4 }}>
                  {i > 0 && <span style={{ opacity:0.5 }}>·</span>}
                  {p.Icon && <p.Icon size={12} color={p.color} />}
                  {p.dot && <Dot color={p.dot} size={7} />}
                  {p.text}
                </span>
              ));
            })()}
          </p>
        </div>
      )}
    </div>
  );
}

// ── Periodo palyginimas (vietoj kasdien atskirai peržiūrėti) ─────────────────
const PERIOD_OPTIONS = [{ k:7, l:"7 d." }, { k:14, l:"14 d." }, { k:30, l:"30 d." }];

function VerdictRow({ text, warn, good }) {
  const color = warn ? "#FF8888" : good ? "#7FFFB0" : "rgba(255,255,255,0.7)";
  return (
    <div style={{ display:"flex", alignItems:"flex-start", gap:8, marginBottom:8 }}>
      <div style={{ width:6, height:6, borderRadius:"50%", background:color, marginTop:5, flexShrink:0 }} />
      <p style={{ fontSize:12, color, margin:0, lineHeight:1.5 }}>{text}</p>
    </div>
  );
}

function StatRow({ Icon, label, value, color }) {
  return (
    <div style={{ background:"rgba(255,255,255,0.08)", borderRadius:12, padding:"10px 12px", display:"flex", justifyContent:"space-between", alignItems:"center" }}>
      <span style={{ fontSize:12, color:"rgba(255,255,255,0.6)", display:"flex", alignItems:"center", gap:5 }}><Icon size={12} />{label}</span>
      <span style={{ fontSize:13, fontWeight:700, color: color || "#fff" }}>{value}</span>
    </div>
  );
}

function PeriodSummary({ client }) {
  const [period, setPeriod] = useState(7);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const from = daysAgoStr(period - 1);
    const today = todayStr();
    Promise.all([
      pb.collection("daily_checkins").getFullList({ filter:`user_id="${client.id}" && date>="${from}" && date<="${today}"`, requestKey:null }).catch(()=>[]),
      pb.collection("sleep_log").getFullList({ filter:`user_id="${client.id}" && date>="${from}" && date<="${today}"`, requestKey:null }).catch(()=>[]),
      pb.collection("water_log").getFullList({ filter:`user_id="${client.id}" && date>="${from}" && date<="${today}"`, requestKey:null }).catch(()=>[]),
      resolveMacroTargets(client.id, client),
    ]).then(([checkins, sleeps, waters, targets]) => {
      if (cancelled) return;
      const doneCheckins = checkins.filter(x => x.is_done === true || x.is_done === "true");
      const stepsData    = checkins.filter(x => x.steps > 0);
      const macroData    = checkins.filter(x => x.protein_g || x.fat_g || x.carbs_g);
      const avgProtein = macroData.length ? macroData.reduce((a,x)=>a+(x.protein_g||0),0)/macroData.length : null;
      const avgFat     = macroData.length ? macroData.reduce((a,x)=>a+(x.fat_g||0),0)/macroData.length : null;
      const avgCarbs   = macroData.length ? macroData.reduce((a,x)=>a+(x.carbs_g||0),0)/macroData.length : null;
      const avgKcal    = macroData.length ? (avgProtein||0)*4 + (avgFat||0)*9 + (avgCarbs||0)*4 : null;
      setStats({
        totalDays: period,
        checkinDays: doneCheckins.length,
        avgNutrition: doneCheckins.length ? doneCheckins.reduce((a,x)=>a+(x.nutrition_score||0),0)/doneCheckins.length : null,
        avgWellbeing: doneCheckins.length ? doneCheckins.reduce((a,x)=>a+(x.wellbeing_score||0),0)/doneCheckins.length : null,
        alcoholDays: doneCheckins.filter(x=>x.alcohol===true).length,
        avgSleep: sleeps.length ? sleeps.reduce((a,x)=>a+(x.hours_slept||0),0)/sleeps.length : null,
        avgWaterL: waters.length ? waters.reduce((a,w)=>a+(w.ml||0),0)/waters.length/1000 : null,
        avgWaterGoalL: waters.length ? waters.reduce((a,w)=>a+(w.goal||2000),0)/waters.length/1000 : null,
        avgSteps: stepsData.length ? Math.round(stepsData.reduce((a,x)=>a+(x.steps||0),0)/stepsData.length) : null,
        stepsGoalRate: stepsData.length ? Math.round(stepsData.filter(x=>x.steps>=7000).length/stepsData.length*100) : null,
        avgKcal, kcalTarget: targets?.targets?.target, macroDays: macroData.length,
      });
      setLoading(false);
    });
    return () => { cancelled = true; };
  }, [client.id, period]);

  return (
    <div>
      <div style={{ display:"flex", gap:6, marginBottom:12 }}>
        {PERIOD_OPTIONS.map(p => (
          <button key={p.k} onClick={()=>setPeriod(p.k)} style={{ flex:1, padding:"9px", borderRadius:12, border:"none", background:period===p.k?"#AD1457":"rgba(255,255,255,0.1)", color:"#fff", fontSize:13, fontWeight:700, cursor:"pointer", fontFamily:"inherit" }}>
            {p.l}
          </button>
        ))}
      </div>

      {loading && <p style={{ color:"rgba(255,255,255,0.4)", fontSize:13, textAlign:"center", padding:"20px 0" }}>Kraunama...</p>}

      {!loading && stats && (() => {
        const verdicts = [];
        const checkinRate = Math.round(stats.checkinDays / stats.totalDays * 100);
        verdicts.push(stats.checkinDays === 0
          ? { text:"Nė karto neužpildė dienos check-in per šį laikotarpį", warn:true }
          : { text:`Check-in užpildytas ${stats.checkinDays}/${stats.totalDays} d. (${checkinRate}%)`, warn: checkinRate < 50, good: checkinRate >= 80 });
        if (stats.avgSteps != null) verdicts.push({ text:`Vid. žingsniai ${stats.avgSteps.toLocaleString()}/d. — tikslas pasiektas ${stats.stepsGoalRate}% dienų`, warn: stats.stepsGoalRate < 40, good: stats.stepsGoalRate >= 70 });
        if (stats.avgSleep != null) { const ok = stats.avgSleep >= 7 && stats.avgSleep <= 9; verdicts.push({ text:`Vid. miegas ${stats.avgSleep.toFixed(1)}h/naktį`, warn: !ok, good: ok }); }
        if (stats.avgWaterL != null) { const ok = stats.avgWaterL >= (stats.avgWaterGoalL||2)*0.85; verdicts.push({ text:`Vid. vanduo ${stats.avgWaterL.toFixed(1)}L/d.`, warn: !ok, good: ok }); }
        if (stats.avgNutrition != null) verdicts.push({ text:`Vid. mitybos vertinimas ${stats.avgNutrition.toFixed(1)}/3`, warn: stats.avgNutrition < 1.8, good: stats.avgNutrition >= 2.5 });
        if (stats.avgWellbeing != null) verdicts.push({ text:`Vid. savijauta ${stats.avgWellbeing.toFixed(1)}/3`, warn: stats.avgWellbeing < 1.8, good: stats.avgWellbeing >= 2.5 });
        if (stats.alcoholDays > 0) verdicts.push({ text:`Alkoholis vartotas ${stats.alcoholDays}x per ${stats.totalDays} d.`, warn: true });
        if (stats.avgKcal != null && stats.kcalTarget) {
          const pct = Math.round(stats.avgKcal / stats.kcalTarget * 100);
          verdicts.push({ text:`Vid. kalorijos ${Math.round(stats.avgKcal)}/${stats.kcalTarget} kcal (${pct}%) iš ${stats.macroDays} d. su suvestais duomenimis`, warn: pct < 80 || pct > 120, good: pct >= 90 && pct <= 110 });
        }

        return (
          <>
            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:8, marginBottom:16 }}>
              <StatRow Icon={Footprints} label="Žingsniai" value={stats.avgSteps ? stats.avgSteps.toLocaleString() : "–"} />
              <StatRow Icon={Moon} label="Miegas" value={stats.avgSleep ? `${stats.avgSleep.toFixed(1)}h` : "–"} />
              <StatRow Icon={Droplet} label="Vanduo" value={stats.avgWaterL!=null ? `${stats.avgWaterL.toFixed(1)}L` : "–"} />
              <StatRow Icon={Flame} label="Kalorijos" value={stats.avgKcal!=null ? `${Math.round(stats.avgKcal)}${stats.kcalTarget?`/${stats.kcalTarget}`:""}` : "–"} />
            </div>

            <p style={{ fontSize:11, fontWeight:700, color:"rgba(255,255,255,0.5)", textTransform:"uppercase", letterSpacing:"0.08em", marginBottom:8 }}>Trumpa išvada</p>
            <div style={{ background:"rgba(0,0,0,0.2)", borderRadius:16, padding:"14px 16px" }}>
              {verdicts.map((v,i) => <VerdictRow key={i} {...v} />)}
            </div>
          </>
        );
      })()}
    </div>
  );
}

function ClientCard({ client, pkgInfo, onOpen }) {
  const badge = !client.onboarding_done
    ? { text: "Anketa nebaigta", color: "#FFC15E", Icon: AlertTriangle }
    : client.track_progress === false
      ? { text: "Nenori rinkti duomenų", color: "rgba(255,255,255,0.45)", Icon: Ban }
      : null;

  const lastOrderLabel = pkgInfo?.lastOrder
    ? new Date(pkgInfo.lastOrder).toLocaleDateString("lt-LT", { year:"numeric", month:"short", day:"numeric" })
    : null;

  const daysToBday  = daysUntilBirthday(client.dob);
  const isBdayToday = daysToBday === 0;
  const isBdaySoon  = daysToBday !== null && daysToBday >= 1 && daysToBday <= 7;

  // Neapmokestintas (nepirkęs paketo) klientas — bent 30d. registruotas, o
  // paskutinis užsakymas (jei buvo) irgi senesnis nei 30d. Naujiems klientams
  // nerodome, kad nebūtų klaidingo įspėjimo iškart po registracijos.
  const DAY_MS = 86400000;
  const clientAgeDays = client.created ? Math.floor((Date.now() - new Date(client.created)) / DAY_MS) : 0;
  const daysSinceOrder = pkgInfo?.lastOrder ? Math.floor((Date.now() - new Date(pkgInfo.lastOrder)) / DAY_MS) : null;
  const noRecentPurchase = clientAgeDays >= 30 && (daysSinceOrder === null || daysSinceOrder >= 30);

  return (
    <button onClick={onOpen} style={{
      position:"relative", width: "100%", background: "rgba(255,255,255,0.08)",
      border: isBdayToday ? "1px solid rgba(255,215,0,0.5)" : noRecentPurchase ? "1px solid rgba(255,90,90,0.4)" : "1px solid rgba(255,255,255,0.15)",
      borderLeft: isBdayToday ? "3px solid #FFD700" : badge ? `3px solid ${badge.color}` : noRecentPurchase ? "3px solid rgba(255,90,90,0.6)" : "1px solid rgba(255,255,255,0.15)",
      borderRadius: 16, padding: "14px 16px", cursor: "pointer", fontFamily: "inherit",
      textAlign: "left", marginBottom: 10, display: "flex", justifyContent: "space-between", alignItems: "center", gap:10,
      animation: isBdayToday ? "bdayCardGlow 2s ease-in-out infinite" : "none",
    }}>
      <style>{BDAY_KEYFRAMES}</style>
      <div style={{ display: "flex", alignItems: "center", gap: 12, minWidth:0 }}>
        <div style={{ position:"relative", width: 40, height: 40, borderRadius: "50%", background: "linear-gradient(135deg,#6D1B3B,#AD1457)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18, flexShrink:0 }}>
          {client.name?.charAt(0) || "?"}
          {isBdayToday && (
            <div style={{
              position:"absolute", top:-8, right:-8, width:22, height:22, borderRadius:"50%",
              background:"linear-gradient(135deg,#FFD700,#FF6EB4)", display:"flex", alignItems:"center", justifyContent:"center",
              boxShadow:"0 0 8px rgba(255,215,0,0.7)", animation:"bdayBadgeBounce 1.4s ease-in-out infinite",
            }}>
              <Cake size={12} color="#fff" />
            </div>
          )}
        </div>
        <div style={{ minWidth:0 }}>
          <p style={{ fontSize: 14, fontWeight: 700, color: "#fff", margin: 0 }}>{client.name}</p>
          {badge ? (
            <p style={{ fontSize: 11, color: badge.color, fontWeight: 600, margin: "2px 0 0", display: "flex", alignItems: "center", gap: 4 }}>
              <badge.Icon size={11} />{badge.text}
            </p>
          ) : (
            <p style={{ fontSize: 11, color: "rgba(255,255,255,0.4)", margin: "2px 0 0" }}>{GOALS.find(g => g.id === client.goal)?.label || "–"}</p>
          )}
          <p style={{ fontSize: 10, color: noRecentPurchase ? "#FF8888" : "rgba(255,255,255,0.35)", fontWeight: noRecentPurchase ? 700 : 400, margin: "3px 0 0", display: "flex", alignItems: "center", gap: 4 }}>
            <Ticket size={10} />{lastOrderLabel ? `Paskutinė užklausa: ${lastOrderLabel}` : "Paketų neužsakinėjo"}
          </p>
        </div>
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 8, flexShrink:0 }}>
        {(isBdayToday || isBdaySoon) && (
          <div style={{
            display:"flex", alignItems:"center", gap:4,
            background: isBdayToday ? "linear-gradient(135deg,#FFD700,#FF6EB4)" : "rgba(255,215,0,0.14)",
            border: isBdayToday ? "none" : "1px solid rgba(255,215,0,0.35)",
            borderRadius:20, padding:"4px 9px",
          }}>
            <Cake size={11} color={isBdayToday ? "#fff" : "#FFD700"} />
            <span style={{ fontSize:11, fontWeight:700, color: isBdayToday ? "#fff" : "#FFD700" }}>
              {isBdayToday ? "Šiandien!" : `už ${daysToBday}d.`}
            </span>
          </div>
        )}
        {pkgInfo?.active && (
          <div style={{
            display:"flex", alignItems:"center", gap:4, background:"rgba(127,255,176,0.14)",
            border:"1px solid rgba(127,255,176,0.35)", borderRadius:20, padding:"4px 9px",
          }}>
            <Ticket size={11} color="#7FFFB0" />
            <span style={{ fontSize:11, fontWeight:700, color:"#7FFFB0" }}>{pkgInfo.active.total - pkgInfo.active.used}/{pkgInfo.active.total}</span>
          </div>
        )}
        <StreakBadge userId={client.id} compact />
        <ChevronRight size={16} color="rgba(255,255,255,0.4)" />
      </div>
    </button>
  );
}

// ── Plano dienos pratimai (admin peržiūra) ────────────────────────────────────
function PlanDayExercises({ dayId }) {
  const [exercises, setExercises] = useState([]);
  const [loading, setLoading]     = useState(true);

  useEffect(() => {
    setLoading(true);
    pb.collection("workout_plan_exercises").getFullList({ filter: `day_id="${dayId}"`, sort: "order", requestKey: null })
      .then(data => { setExercises(data); setLoading(false); }).catch(() => setLoading(false));
  }, [dayId]);

  if (loading) return <p style={{ fontSize:11, color:"rgba(255,255,255,0.4)", margin:"8px 0 0" }}>Kraunama...</p>;
  if (!exercises.length) return <p style={{ fontSize:11, color:"rgba(255,255,255,0.4)", margin:"8px 0 0" }}>Pratimų nėra</p>;

  return (
    <div style={{ display:"flex", flexDirection:"column", gap:6, marginTop:4 }}>
      {exercises.map((ex, i) => (
        <div key={ex.id} style={{ background:"rgba(0,0,0,0.2)", borderRadius:10, padding:"10px 12px", display:"flex", justifyContent:"space-between", alignItems:"center" }}>
          <div style={{ display:"flex", flexDirection:"column", gap:4 }}>
            <p style={{ fontSize:12, fontWeight:700, color:"#fff", margin:0 }}>{i+1}. {ex.exercise_name}</p>
            <ExerciseSummary ex={ex} />
          </div>
          <span style={{ fontSize:10, color:ex.category==="cardio"?"#89CFF0":"#FFB3C6", background:"rgba(255,255,255,0.08)", padding:"2px 8px", borderRadius:6 }}>{ex.muscle}</span>
        </div>
      ))}
    </div>
  );
}

// ── Kliento profilis su kalendoriumi ─────────────────────────────────────────
// embedded=true (desktop/iPad split-pane režimas) — rodomas šalia kliento
// sąrašo, ne per visą ekraną, kad šoninis meniu liktų matomas.
function ClientDetail({ client, onClose, embedded, autoOpenLiveTraining }) {
  const isWide = useIsWide();
  const [selectedDate, setSelectedDate] = useState(todayStr());
  const [checkinDates, setCheckinDates] = useState([]);
  const [calendarView, setCalendarView] = useState("day"); // "day" | "period"
  const [planBuilder, setPlanBuilder] = useState(null); // null=uždaryta, true=naujas, objektas=redaguoti esamą
  const [showProgressCompare, setShowProgressCompare] = useState(false);
  const [showMeasurements, setShowMeasurements] = useState(false);
  // Pradinė reikšmė tik iš autoOpenLiveTraining props — šis komponentas
  // sąmoningai priverstinai perkuriamas (key pasikeičia) kaskart, kai reikia
  // iš karto atidaryti gyvą treniruotę (žr. openClientLive() AdminPanel.js),
  // todėl šis useState visada "pagaus" naują props reikšmę iš naujo.
  const [showLiveTraining, setShowLiveTraining] = useState(!!autoOpenLiveTraining);
  const [showClientInfo, setShowClientInfo] = useState(false);
  const [showReport, setShowReport] = useState(false);
  const [allPlans, setAllPlans]           = useState([]);
  const [planForDate, setPlanForDate]     = useState(null);
  const [planDays, setPlanDays]           = useState([]);
  const [selectedPlanDay, setSelectedPlanDay] = useState(null);
  const [planExercises, setPlanExercises] = useState([]);
  const [workoutLogs, setWorkoutLogs]     = useState({});
  const [planLoading, setPlanLoading]     = useState(false);

  function reloadPlans() {
    pb.collection("workout_plans").getFullList({
      filter: `user_id="${client.id}" && is_active=true`, sort: "created", requestKey: null,
    }).then(setAllPlans).catch(() => {});
  }

  useEffect(() => {
    pb.collection("daily_checkins").getFullList({
      filter: `user_id="${client.id}" && is_done=true`, fields: "date", requestKey: null,
    }).then(items => setCheckinDates(items.map(i => i.date))).catch(() => {});
    reloadPlans();
  }, [client.id]);

  // Kai keičiasi data — rasti planą kuris TUO METU galiojo
  useEffect(() => {
    let cancelled = false;
    async function loadPlanForDate() {
      setPlanForDate(null); setPlanDays([]); setSelectedPlanDay(null); setPlanExercises([]); setWorkoutLogs({});
      if (!allPlans.length) return;

      const validPlans = allPlans.filter(p =>
        (p.start_date || "").slice(0,10) <= selectedDate && (p.end_date || "").slice(0,10) >= selectedDate
      );
      const plan = validPlans[validPlans.length - 1] || null;
      if (cancelled) return;
      setPlanForDate(plan);
      if (!plan) return;

      setPlanLoading(true);
      const days = await pb.collection("workout_plan_days").getFullList({
        filter: `plan_id="${plan.id}"`, sort: "day_number", requestKey: null,
      }).catch(() => []);
      if (cancelled) return;
      setPlanDays(days);

      const firstDay = days[0] || null;
      setSelectedPlanDay(firstDay);
      if (!firstDay) { setPlanLoading(false); return; }

      const exs = await pb.collection("workout_plan_exercises").getFullList({
        filter: `day_id="${firstDay.id}"`, sort: "order_num", requestKey: null,
      }).catch(() => []);
      if (cancelled) return;
      setPlanExercises(exs);

      const logs = await pb.collection("workout_logs_client").getFullList({
        filter: `user_id="${client.id}" && plan_day_id="${firstDay.id}" && calendar_date="${selectedDate}"`,
        requestKey: null,
      }).catch(() => []);
      if (cancelled) return;
      const logsMap = {};
      logs.forEach(l => { logsMap[l.plan_exercise_id] = l; });
      setWorkoutLogs(logsMap);
      setPlanLoading(false);
    }
    loadPlanForDate();
    return () => { cancelled = true; };
  }, [selectedDate, allPlans, client.id]);

  // Kai treneris rankiniu būdu pasirenka kitą plano dieną tab'uose
  async function handleSelectPlanDay(day) {
    setSelectedPlanDay(day);
    setPlanLoading(true);
    setPlanExercises([]); setWorkoutLogs({});
    const exs = await pb.collection("workout_plan_exercises").getFullList({
      filter: `day_id="${day.id}"`, sort: "order_num", requestKey: null,
    }).catch(() => []);
    setPlanExercises(exs);
    const logs = await pb.collection("workout_logs_client").getFullList({
      filter: `user_id="${client.id}" && plan_day_id="${day.id}" && calendar_date="${selectedDate}"`,
      requestKey: null,
    }).catch(() => []);
    const logsMap = {};
    logs.forEach(l => { logsMap[l.plan_exercise_id] = l; });
    setWorkoutLogs(logsMap);
    setPlanLoading(false);
  }

  const doneCount = planExercises.filter(ex => workoutLogs[ex.id]?.is_done).length;

  return (
    <div style={{ position: embedded ? "absolute" : "fixed", inset: 0, zIndex: embedded ? 700 : 1150, background: "linear-gradient(160deg,#2d0a1a 0%,#6D1B3B 40%,#AD1457 100%)", overflowY: "auto", overscrollBehavior: "contain", WebkitOverflowScrolling: "touch", fontFamily: "-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif" }}>
      {planBuilder && (
        <WorkoutPlanBuilder client={client} plan={planBuilder===true?undefined:planBuilder} onClose={()=>setPlanBuilder(null)} onSaved={()=>{ setPlanBuilder(null); reloadPlans(); }} />
      )}
      {showProgressCompare && (
        <ProgressCompare client={client} onClose={()=>setShowProgressCompare(false)} />
      )}
      {showMeasurements && (
        <ClientMeasurements client={client} onClose={()=>setShowMeasurements(false)} />
      )}
      {showLiveTraining && (
        <LiveTraining client={client} onClose={()=>setShowLiveTraining(false)} />
      )}
      {showClientInfo && (
        <ClientInfo client={client} onClose={()=>setShowClientInfo(false)} />
      )}
      {showReport && (
        <ClientReport client={client} onClose={()=>setShowReport(false)} />
      )}
      <style>{BDAY_KEYFRAMES}</style>
      <div style={{ background: "rgba(0,0,0,0.2)", borderBottom: "1px solid rgba(255,255,255,0.1)", padding: "max(env(safe-area-inset-top), 16px) 20px 16px", display: "flex", alignItems: "center", gap: 12, position: "sticky", top: 0, zIndex: 10 }}>
        <button onClick={onClose} style={{ background: "rgba(255,255,255,0.1)", border: "1px solid rgba(255,255,255,0.15)", borderRadius: 12, padding: "8px 14px", color: "#fff", fontSize: 14, cursor: "pointer", display:"flex", alignItems:"center", gap:5 }}><ChevronLeft size={14} />Atgal</button>
        <div>
          <h1 style={{ fontSize: 16, fontWeight: 700, color: "#fff", margin: 0 }}>{client.name}</h1>
          <p style={{ fontSize: 10, color: "rgba(255,255,255,0.4)", margin: 0 }}>{client.email}</p>
        </div>
      </div>

      <div style={{ maxWidth: embedded ? 640 : (isWide ? 720 : 480), margin: "0 auto", padding: "16px" }}>

        {/* Veiksmų kortelės */}
        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10, marginBottom:16 }}>
          <button onClick={() => setShowLiveTraining(true)} style={{ gridColumn:"1 / -1", padding:"16px", borderRadius:18, background:"linear-gradient(135deg,#AD1457,#E91E8C)", border:"none", color:"#fff", cursor:"pointer", fontFamily:"inherit", display:"flex", alignItems:"center", gap:12, textAlign:"left" }}>
            <span style={{ width:42, height:42, borderRadius:"50%", background:"rgba(255,255,255,0.2)", display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>
              <Dumbbell size={20} />
            </span>
            <span style={{ flex:1 }}>
              <span style={{ display:"flex", alignItems:"center", gap:6, fontSize:15, fontWeight:700 }}>
                Gyva treniruotė
                <Dot color="#FF6B6B" size={8} style={{ animation:"livePulse 1.2s ease-in-out infinite" }} />
              </span>
              <span style={{ display:"block", fontSize:11, color:"rgba(255,255,255,0.75)", marginTop:2 }}>Pradėti arba tęsti šiandienos treniruotę</span>
            </span>
            <ChevronRight size={16} color="rgba(255,255,255,0.6)" />
          </button>
          {[
            { key:"clientInfo", Icon:Clipboard, color:"#89CFF0", title:"Anketa", sub:"Profilis ir tikslai", onClick:()=>setShowClientInfo(true) },
            { key:"measurements", Icon:Ruler, color:"#7FFFB0", title:"Matavimai", sub:"Svoris ir kūno rodikliai", onClick:()=>setShowMeasurements(true) },
            { key:"photos", Icon:Camera, color:"#FFD700", title:"Nuotraukos", sub:"Progreso palyginimas", onClick:()=>setShowProgressCompare(true) },
            { key:"report", Icon:BarChart, color:"#FF9E7A", title:"Ataskaita", sub:"Formuoti suvestinę", onClick:()=>setShowReport(true) },
          ].map(t => (
            <button key={t.key} onClick={t.onClick} style={{ padding:"14px", background:"rgba(255,255,255,0.08)", color:"#fff", border:"1px solid rgba(255,255,255,0.15)", borderRadius:16, cursor:"pointer", fontFamily:"inherit", display:"flex", flexDirection:"column", alignItems:"flex-start", gap:8 }}>
              <span style={{ width:36, height:36, borderRadius:"50%", background:`${t.color}2a`, display:"flex", alignItems:"center", justifyContent:"center" }}>
                <t.Icon size={16} color={t.color} />
              </span>
              <span>
                <span style={{ display:"block", fontSize:13, fontWeight:700 }}>{t.title}</span>
                <span style={{ display:"block", fontSize:10, color:"rgba(255,255,255,0.4)", marginTop:1 }}>{t.sub}</span>
              </span>
            </button>
          ))}
        </div>

        {/* Sporto plano kortelė */}
        <div style={{ marginBottom: 16, background: "rgba(26,71,49,0.5)", borderRadius: 16, padding: "14px 16px", border: "1px solid rgba(127,255,176,0.2)" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: planForDate ? 10 : 0 }}>
            <div>
              {planForDate ? (
                <>
                  <p style={{ fontSize: 13, fontWeight: 700, color: "#7FFFB0", margin: "0 0 2px", display:"flex", alignItems:"center", gap:6 }}><Dumbbell size={14} />{planForDate.plan_name}</p>
                  <p style={{ fontSize: 11, color: "rgba(255,255,255,0.4)", margin: 0, display:"flex", alignItems:"center", gap:4 }}>
                    {planForDate.start_date} <ChevronRight size={11} /> {planForDate.end_date}
                    {doneCount > 0 && <span style={{ marginLeft: 6, color: "#7FFFB0" }}>· {doneCount}/{planExercises.length} atlikta</span>}
                  </p>
                </>
              ) : (
                <p style={{ fontSize: 13, color: "rgba(255,255,255,0.5)", margin: 0, display:"flex", alignItems:"center", gap:6 }}>
                  <Dumbbell size={14} />{allPlans.length > 0 ? "Šiai datai planas negaliojo" : "Sporto plano nėra"}
                </p>
              )}
            </div>
            <div style={{ display:"flex", gap:6, flexShrink:0 }}>
              {planForDate && (
                <button onClick={() => setPlanBuilder(planForDate)} style={{ background: "rgba(255,255,255,0.1)", border: "1px solid rgba(255,255,255,0.2)", borderRadius: 8, padding: "6px 12px", color: "#fff", fontSize: 11, cursor: "pointer", fontFamily: "inherit" }}>Redaguoti</button>
              )}
              <button onClick={() => setPlanBuilder(true)} style={{ background: "rgba(255,255,255,0.1)", border: "1px solid rgba(255,255,255,0.2)", borderRadius: 8, padding: "6px 12px", color: "#fff", fontSize: 11, cursor: "pointer", fontFamily: "inherit" }}>+ Naujas</button>
            </div>
          </div>

          {/* Dienų tab'ai */}
          {planForDate && planDays.length > 0 && (
            <div style={{ display: "flex", gap: 6, overflowX: "auto", marginBottom: 10, paddingBottom: 2 }}>
              {planDays.map(d => (
                <button key={d.id} onClick={() => handleSelectPlanDay(d)}
                  style={{ padding: "6px 12px", borderRadius: 20, border: "none", background: selectedPlanDay?.id === d.id ? "#276749" : "rgba(255,255,255,0.1)", color: "#fff", fontSize: 11, fontWeight: 700, cursor: "pointer", whiteSpace: "nowrap", fontFamily: "inherit", flexShrink: 0 }}>
                  {d.day_label}
                </button>
              ))}
            </div>
          )}

          {/* Pratimai */}
          {planLoading && <p style={{ fontSize: 11, color: "rgba(255,255,255,0.4)", margin: "4px 0 0" }}>Kraunama...</p>}
          {!planLoading && planForDate && selectedPlanDay && planExercises.length === 0 && (
            <p style={{ fontSize: 11, color: "rgba(255,255,255,0.4)", margin: "4px 0 0" }}>Šiai dienai pratimų nėra</p>
          )}
          {!planLoading && planExercises.map((ex, i) => {
            const done = workoutLogs[ex.id]?.is_done;
            return (
              <div key={ex.id} style={{ background: done ? "rgba(127,255,176,0.08)" : "rgba(0,0,0,0.2)", borderRadius: 10, padding: "9px 12px", marginTop: 6, display: "flex", justifyContent: "space-between", alignItems: "center", border: `1px solid ${done ? "rgba(127,255,176,0.25)" : "transparent"}` }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  {done ? <CheckCircle size={16} color="#7FFFB0" /> : <span style={{ display:"inline-block", width:13, height:13, borderRadius:4, border:"1.75px solid rgba(255,255,255,0.35)" }} />}
                  <div>
                    <p style={{ fontSize: 12, fontWeight: 600, color: done ? "#7FFFB0" : "#fff", margin: "0 0 4px", textDecoration: done ? "line-through" : "none", opacity: done ? 0.8 : 1 }}>
                      {i+1}. {ex.exercise_name}
                    </p>
                    <ExerciseSummary ex={ex} />
                    {ex.trainer_note && <p style={{ fontSize: 11, color: "#FF6EB4", margin: "4px 0 0", fontStyle: "italic" }}>{ex.trainer_note}</p>}
                  </div>
                </div>
                <span style={{ fontSize: 10, color: ex.category==="cardio"?"#89CFF0":"#FFB3C6", background:"rgba(255,255,255,0.08)", padding:"2px 8px", borderRadius:6 }}>{ex.muscle}</span>
              </div>
            );
          })}
        </div>

        {/* Kalendorius / periodo palyginimas */}
        <div style={{ display:"flex", gap:6, marginBottom:12 }}>
          {[{k:"day",l:"Diena"},{k:"period",l:"Periodo palyginimas"}].map(t => (
            <button key={t.k} onClick={()=>setCalendarView(t.k)} style={{ flex:1, padding:"9px", borderRadius:12, border:"none", background:calendarView===t.k?"#AD1457":"rgba(255,255,255,0.1)", color:"#fff", fontSize:13, fontWeight:700, cursor:"pointer", fontFamily:"inherit" }}>
              {t.l}
            </button>
          ))}
        </div>

        {calendarView === "day" ? (
          <>
            <p style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", color: "rgba(255,255,255,0.6)", margin: "0 0 8px" }}>Pasirinkite dieną</p>
            <MiniCalendar selectedDate={selectedDate} onSelect={setSelectedDate} checkinDates={checkinDates} />
            <DayView client={client} date={selectedDate} />
          </>
        ) : (
          <PeriodSummary client={client} />
        )}
      </div>
    </div>
  );
}

// ── Naujas klientas forma ──────────────────────────────────────────────────────
function NewClientForm({ onSave, onCancel }) {
  const [form,   setForm]   = useState({ name: "", email: "", password: "" });
  const [saving, setSaving] = useState(false);
  const [error,  setError]  = useState("");
  const set = k => v => setForm(f => ({...f, [k]: v}));

  async function handleSave() {
    if (!form.name || !form.email || !form.password) { setError("Visi laukai privalomi."); return; }
    if (form.password.length < 6) { setError("Slaptažodis min. 6 simboliai."); return; }
    setSaving(true);
    try {
      const newUser = await adminCreateUser(form.email, form.password);
      await pb.collection("users").update(newUser.id, { name: form.name });
      onSave();
    } catch(e) {
      setError(e?.response?.data?.email?.message || e.message || "Klaida");
    }
    setSaving(false);
  }

  const inp = { width: "100%", padding: "12px 14px", border: "1.5px solid rgba(255,255,255,0.2)", borderRadius: 12, fontSize: 14, color: "#fff", background: "rgba(255,255,255,0.07)", outline: "none", fontFamily: "inherit", boxSizing: "border-box" };

  return (
    <div>
      {["name","email","password"].map(k => (
        <div key={k} style={{ marginBottom: 12 }}>
          <label style={{ fontSize: 10, fontWeight: 700, color: "rgba(255,255,255,0.75)", textTransform: "uppercase", display: "block", marginBottom: 5 }}>
            {k === "name" ? "Vardas Pavardė" : k === "email" ? "El. paštas" : "Slaptažodis"}
          </label>
          <input type={k === "password" ? "password" : "text"} value={form[k]}
            onChange={e => set(k)(e.target.value)}
            style={inp} />
        </div>
      ))}
      {error && <p style={{ color: "#FF6B6B", fontSize: 13, marginBottom: 10 }}>{error}</p>}
      <div style={{ display: "flex", gap: 10 }}>
        <button onClick={onCancel} style={{ flex: 1, padding: "12px", borderRadius: 12, border: "1.5px solid rgba(255,255,255,0.3)", background: "transparent", color: "#fff", fontSize: 14, cursor: "pointer", fontFamily: "inherit" }}>Atšaukti</button>
        <button onClick={handleSave} disabled={saving} style={{ flex: 2, padding: "12px", borderRadius: 12, background: "linear-gradient(135deg,#6D1B3B,#AD1457)", color: "#fff", border: "none", fontSize: 14, fontWeight: 700, cursor: "pointer", fontFamily: "inherit", opacity: saving ? 0.7 : 1 }}>
          {saving ? "Kuriama..." : "Sukurti klientą"}
        </button>
      </div>
    </div>
  );
}

// ── Pagrindinis Admin komponentas ─────────────────────────────────────────────
export default function AdminPanel({ user, onLogout }) {
  const isDesktop = useIsDesktop();
  const isWide = useIsWide();
  const [clients,     setClients]     = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [view,        setView]        = useState("list");
  const [openClient,  setOpenClient]  = useState(null);
  const [showBookings, setShowBookings] = useState(false);
  const [showStats, setShowStats]       = useState(false);
  const [showPresets, setShowPresets]   = useState(false);
  const [showPackages, setShowPackages] = useState(false);
  const [adminBadges, setAdminBadges]   = useState({ bookings:0, packages:0 });
  const [pkgSummary,  setPkgSummary]    = useState({});
  const [dashExtra,   setDashExtra]     = useState({ todayBookings:0, weekPackages:0 });
  const [todaySessions, setTodaySessions] = useState([]);
  const [notInCalendar, setNotInCalendar] = useState([]);
  const [calendarError, setCalendarError] = useState("");
  const [nowTime, setNowTime] = useState(() => new Date().toTimeString().slice(0,5));

  useEffect(() => {
    const t = setInterval(() => setNowTime(new Date().toTimeString().slice(0,5)), 60000);
    return () => clearInterval(t);
  }, []);
  const [clientSearch, setClientSearch] = useState("");
  const [visibleClients, setVisibleClients] = useState(8);
  const [refreshing, setRefreshing] = useState(false);
  const [liveSessionInfo, setLiveSessionInfo] = useState(() => getActiveLiveSession());
  const [pendingAutoOpenLiveTraining, setPendingAutoOpenLiveTraining] = useState(false);
  const [clientDetailNonce, setClientDetailNonce] = useState(0);

  useEffect(() => subscribeLiveSession(setLiveSessionInfo), []);

  // Atkuria indikatorių po puslapio perkrovimo — JS atmintyje saugoma būsena
  // tada dingsta, bet pati sesija duombazėje lieka. Patikrinama vieną kartą,
  // ar šiandien yra pradėta, bet nebaigta gyva treniruotė, kad ir kuriam
  // klientui — jei LiveTraining.js jos dar nespėjo pati pranešti.
  useEffect(() => {
    pb.collection("live_sessions").getFirstListItem(`date="${todayStr()}" && completed=false`, { requestKey: null })
      .then(async sess => {
        const c = await pb.collection("users").getOne(sess.user_id, { requestKey: null }).catch(() => null);
        if (c) setActiveLiveSession({ clientId: c.id, clientName: c.name });
      })
      .catch(() => {});
  }, []);

  async function handleRefresh() {
    setRefreshing(true);
    await loadClients();
    setRefreshing(false);
  }

  // attemptExit() — jei kuris nors atidarytas ekranas (pvz. gyva treniruotė
  // su nebaigta sesija) užregistravęs išėjimo sargą, pirma parodomas jo
  // patvirtinimo langas, o pati navigacija įvyksta tik vartotojui apsisprendus.
  function openAdminTab(tab) {
    attemptExit(() => {
      setShowBookings(tab === "bookings");
      setShowStats(tab === "stats");
      setShowPresets(tab === "presets");
      setShowPackages(tab === "packages");
      // Pereinant per šoninį meniu į kitą skiltį, uždaryti atidarytą kliento
      // kortelę (su viduje atidarytais įrankiais) — kitaip ji likdavo rodoma
      // virš naujos skilties ir uždengdavo ją (desktop/iPad split-pane klaida).
      setOpenClient(null);
    });
  }

  function goToNewClient() {
    attemptExit(() => setView("new"));
  }

  function handleLogoutClick() {
    attemptExit(onLogout);
  }

  // Bendras "atidaryti/uždaryti klientą" veiksmas (c=null uždaro) — naudojamas
  // visur, kur paspaudus kliento kortelę/eilutę pereinama prie jo (ar grįžtama
  // į sąrašą), kad tai irgi eitų per attemptExit() sargą. Visada aiškiai
  // išjungia "iš karto atidaryti gyvą treniruotę" ketinimą — jį įjungia tik
  // openClientLive() žemiau, kitaip jis liktų "įstrigęs" kitiems klientams.
  function openClientSafe(c) {
    attemptExit(() => { setPendingAutoOpenLiveTraining(false); setOpenClient(c); });
  }

  // Atidaro klientą TIESIOG jo gyvoje treniruotėje — naudojama paspaudus
  // nuolatinį "Treniruotė vyksta" indikatorių arba šiandien vykstančios
  // (dabar laiku sutampančios) rezervacijos eilutę. nonce priverčia
  // ClientDetail iš naujo sumontuoti (ir pačiam perskaityti autoOpen props),
  // net jei tas pats klientas jau atidarytas kitame įrankyje.
  function openClientLive(c) {
    attemptExit(() => {
      setPendingAutoOpenLiveTraining(true);
      setClientDetailNonce(n => n + 1);
      setOpenClient(c);
    });
  }

  function goToLiveSessionBanner() {
    if (!liveSessionInfo) return;
    const c = clients.find(cl => cl.id === liveSessionInfo.clientId);
    if (c) openClientLive(c);
  }

  // PocketBase įrašymas PIRMIAU, failo atsisiuntimas PASKUI — mobiliuosiuose
  // .ics atsisiuntimas dažnai atidaro sisteminį "Pridėti į kalendorių" langą
  // ir nustumia programėlę į foną, o tai gali nutraukti tuo metu dar
  // vykstantį PocketBase užklausimą (rodydavosi kaip "failas atsisiuntė, bet
  // pažymėti nepavyko" būtent telefonuose, kompiuteryje veikdavo be klaidų).
  async function handleAddToCalendar(booking) {
    setCalendarError("");
    const clientName = clients.find(c => c.id === booking.client_id)?.name || "Klientas";
    const ok = await pb.collection("bookings").update(booking.id, { added_to_calendar: true }).then(() => true).catch(() => false);
    if (ok) {
      setNotInCalendar(prev => prev.filter(b => b.id !== booking.id));
      downloadIcal(booking, clientName);
    } else {
      setCalendarError("Nepavyko pažymėti kaip pridėtos — patikrinkite interneto ryšį ir bandykite dar kartą.");
    }
  }

  const activeAdminTab = showBookings ? "bookings"
    : showStats    ? "stats"
    : showPresets  ? "presets"
    : showPackages ? "packages"
    : "clients";

  // Rezervacijų signalai (šiandienos sesijos, laukiančios, nepridėtos į kalendorių) —
  // atskira funkcija, kad tą patį atnaujinimą galėtume paleisti tiek pradiniame
  // užkrovime, tiek realtime prenumeratoje (žr. žemiau) — kai du treneriai/adminai
  // dirba iš skirtingų paskyrų, vieno veiksmas (pvz. pažymėjimas "pridėta į
  // kalendorių") turi iškart atsispindėti kito ekrane, ne tik po rankinio "Atnaujinti".
  const refreshBookingSignals = useCallback(() => {
    Promise.all([
      pb.collection("bookings").getFullList({ filter:'status="pending"', requestKey:null }).catch(()=>[]),
      pb.collection("training_packages").getFullList({ filter:'status="pending"', requestKey:null }).catch(()=>[]),
    ]).then(([bks, pkgs]) => {
      setAdminBadges({ bookings: bks.length, packages: pkgs.length });
    });
    const today = todayStr();
    pb.collection("bookings").getFullList({ filter:`date="${today}"`, requestKey:null }).catch(()=>[]).then(bks => {
      const approvedToday = bks.filter(b => b.status === "approved").sort((a,b) => (a.start_time||"").localeCompare(b.start_time||""));
      setDashExtra(d => ({ ...d, todayBookings: approvedToday.length }));
      setTodaySessions(approvedToday);
    });
    pb.collection("bookings").getFullList({ filter:'status="approved" && added_to_calendar=false', sort:"date,start_time", requestKey:null }).catch(()=>[]).then(setNotInCalendar);
  }, []);

  const loadClients = useCallback(async () => {
    const data = await pb.collection("users").getFullList({ filter: 'role="client"', sort: "-created", requestKey: null });
    setClients(data || []);
    setLoading(false);
    refreshBookingSignals();
    // Kiekvieno kliento paskutinė paketo užklausa + aktyvus paketas
    Promise.all([
      pb.collection("training_packages").getFullList({ sort:"-created", requestKey:null }).catch(()=>[]),
      fetchAllDayVacations(),
    ]).then(([pkgs, vacations]) => {
      const summary = {};
      pkgs.forEach(p => {
        if (!summary[p.client_id]) summary[p.client_id] = { lastOrder: p.created, active: null };
        // Sudedame VISUS dar neišnaudotus IR NEPASIBAIGUSIO galiojimo patvirtintus
        // paketus, ne tik naujausią — klientas gali vienu metu turėti kelis
        // galiojančius paketus. Pasibaigusio galiojimo (net su likusiais
        // kreditais) čia nebelaikomas aktyviu — kitaip šis ženkliukas rodytų
        // klientą turintį treniruočių, kai realiai jomis pasinaudoti jis
        // nebegali (žr. tą pačią patikrą TrainingPackages.js/PackageAdmin.js).
        if (p.status === "approved" && p.credits_used < p.credits_total) {
          const dl = effectiveDeadline(p, vacations);
          const expired = dl && pkgDaysUntil(dl) < 0;
          if (!expired) {
            if (!summary[p.client_id].active) {
              summary[p.client_id].active = { total: p.credits_total, used: p.credits_used };
            } else {
              summary[p.client_id].active.total += p.credits_total;
              summary[p.client_id].active.used += p.credits_used;
            }
          }
        }
      });
      setPkgSummary(summary);
      const weekAgo = daysAgoStr(7);
      const weekPackages = pkgs.filter(p => p.status === "approved" && (p.created || "") >= weekAgo).length;
      setDashExtra(d => ({ ...d, weekPackages }));
    });
  }, [refreshBookingSignals]);

  useEffect(() => { loadClients(); }, [loadClients]);

  // Realtime — kai KITAS adminas (kita paskyra) pakeičia rezervaciją (patvirtina,
  // pažymi pridėta į kalendorių ir pan.), šis langas atsinaujina iškart, o ne tik
  // paspaudus "Atnaujinti".
  useEffect(() => {
    let unsub;
    pb.collection("bookings").subscribe("*", () => refreshBookingSignals()).then(fn => { unsub = fn; }).catch(() => {});
    return () => { unsub?.(); };
  }, [refreshBookingSignals]);

  if (view === "new") return (
    <div style={{ minHeight: "100vh", background: "linear-gradient(160deg,#2d0a1a 0%,#6D1B3B 40%,#AD1457 100%)", fontFamily: "-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif", paddingBottom: 48 }}>
      <div style={{ background: "rgba(0,0,0,0.2)", borderBottom: "1px solid rgba(255,255,255,0.1)", padding: "max(env(safe-area-inset-top), 16px) 20px 16px", display: "flex", alignItems: "center", gap: 12 }}>
        <button onClick={() => setView("list")} style={{ background: "rgba(255,255,255,0.1)", border: "1px solid rgba(255,255,255,0.15)", borderRadius: 12, padding: "8px 12px", color: "#fff", fontSize: 14, cursor: "pointer", display:"flex", alignItems:"center", gap:5 }}><ChevronLeft size={14} />Atgal</button>
        <h1 style={{ fontSize: 17, fontWeight: 700, color: "#fff", margin: 0 }}>Naujas klientas</h1>
      </div>
      <div style={{ maxWidth: isWide ? 640 : 480, margin: "0 auto", padding: "16px" }}>
        <NewClientForm onSave={() => { setView("list"); loadClients(); }} onCancel={() => setView("list")} />
      </div>
    </div>
  );

  // Telefone ir iPad portrait režime (<1000px) — ta pati struktūra kaip
  // visada (bottom tab bar), tik turinio stulpelis šiek tiek platesnis
  // vidutinio pločio ekranams (isWide, ≥680px) — telefonui (<680px)
  // visiškai jokio pakeitimo, pikselis tikslus su ankstesne versija.
  if (!isDesktop) return (
    <div style={{ height: "100%", display: "flex", flexDirection: "column", background: "linear-gradient(160deg,#2d0a1a 0%,#6D1B3B 40%,#AD1457 100%)", fontFamily: "-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif" }}>
      {/* Scrollinamas turinys — antraštė + sąrašas vienoje scroll zonoje */}
      <div style={{ flex: 1, minHeight: 0, overflowY: "auto", WebkitOverflowScrolling: "touch", paddingBottom: 80 }}>
        {/* Header — paprastas */}
        <div style={{ background: "rgba(0,0,0,0.2)", borderBottom: "1px solid rgba(255,255,255,0.1)", paddingTop: "max(env(safe-area-inset-top), 16px)", paddingLeft: 20, paddingRight: 20, paddingBottom: 16 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <img src="/logo.png" alt="Coach Vilma" style={{ width: 34, height: 34, objectFit: "contain", borderRadius: 8 }} />
              <div>
                <h1 style={{ fontSize: 15, fontWeight: 700, color: "#fff", margin: 0 }}>Vilma · Admin</h1>
                <p style={{ fontSize: 10, color: "rgba(255,255,255,0.4)", margin: 0 }}>Trenerės panelė</p>
              </div>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <button onClick={handleRefresh} disabled={refreshing} aria-label="Atnaujinti" style={{ background: "rgba(255,255,255,0.08)", border: "1px solid rgba(255,255,255,0.15)", borderRadius: 12, width: 32, height: 32, display: "flex", alignItems: "center", justifyContent: "center", color: "rgba(255,255,255,0.85)", cursor: refreshing ? "default" : "pointer" }}>
                <Refresh size={15} style={{ animation: refreshing ? "spin 0.7s linear infinite" : "none", transformOrigin: "center" }} />
              </button>
              <button onClick={handleLogoutClick} style={{ background: "rgba(255,255,255,0.08)", border: "1px solid rgba(255,255,255,0.15)", borderRadius: 12, padding: "7px 12px", color: "rgba(255,255,255,0.7)", fontSize: 11, cursor: "pointer", fontFamily: "inherit" }}>Atsijungti</button>
            </div>
          </div>
        </div>

        <div style={{ maxWidth: isWide ? 720 : 480, margin: "0 auto", padding: "16px" }}>
          <PushPermissionPrompt userId={user.id} />

          {(() => {
            const todayBdays = clients.filter(c => daysUntilBirthday(c.dob) === 0);
            const weekBdays = clients
              .map(c => ({ c, d: daysUntilBirthday(c.dob) }))
              .filter(x => x.d != null && x.d >= 1 && x.d <= 7)
              .sort((a, b) => a.d - b.d);
            if (!todayBdays.length && !weekBdays.length) return null;
            return (
              <div style={{ background: "linear-gradient(135deg,rgba(255,215,0,0.12),rgba(255,110,180,0.08))", border: "1px solid rgba(255,215,0,0.3)", borderRadius: 18, padding: "14px 16px", marginBottom: 16 }}>
                <p style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", color: "#FFD700", margin: "0 0 10px", display: "flex", alignItems: "center", gap: 6 }}>
                  <Cake size={12} />Gimtadieniai
                </p>
                <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                  {todayBdays.map(c => (
                    <button key={c.id} onClick={() => openClientSafe(c)} style={{ display: "flex", alignItems: "center", gap: 8, background: "linear-gradient(135deg,rgba(255,215,0,0.2),rgba(255,110,180,0.16))", border: "1px solid rgba(255,215,0,0.45)", borderRadius: 12, padding: "9px 12px", cursor: "pointer", fontFamily: "inherit", textAlign: "left", width: "100%", animation: "bdayCardGlow 2s ease-in-out infinite" }}>
                      <Cake size={14} color="#FFD700" style={{ flexShrink: 0 }} />
                      <span style={{ fontSize: 13, fontWeight: 700, color: "#fff", flex: 1 }}>{c.name}</span>
                      <span style={{ fontSize: 10, fontWeight: 800, color: "#FFD700", background: "rgba(255,215,0,0.22)", borderRadius: 20, padding: "2px 8px", flexShrink: 0 }}>ŠIANDIEN</span>
                      <ChevronRight size={13} color="rgba(255,255,255,0.4)" />
                    </button>
                  ))}
                  {weekBdays.map(({ c, d }) => (
                    <button key={c.id} onClick={() => openClientSafe(c)} style={{ display: "flex", alignItems: "center", gap: 8, background: "rgba(255,255,255,0.06)", border: "1px solid transparent", borderRadius: 12, padding: "9px 12px", cursor: "pointer", fontFamily: "inherit", textAlign: "left", width: "100%" }}>
                      <Cake size={13} color="rgba(255,215,0,0.7)" style={{ flexShrink: 0 }} />
                      <span style={{ fontSize: 13, fontWeight: 600, color: "#fff", flex: 1 }}>{c.name}</span>
                      <span style={{ fontSize: 11, color: "rgba(255,255,255,0.5)", flexShrink: 0 }}>{d === 1 ? "rytoj" : `už ${d} d.`}</span>
                      <ChevronRight size={13} color="rgba(255,255,255,0.4)" />
                    </button>
                  ))}
                </div>
              </div>
            );
          })()}

          {(() => {
            const upcoming = todaySessions.filter(b => (b.end_time || b.start_time) >= nowTime);
            const showCard = upcoming.length > 0 || adminBadges.bookings > 0 || adminBadges.packages > 0;
            if (!showCard) return null;
            return (
            <div style={{ background: "rgba(255,255,255,0.06)", borderRadius: 18, padding: "14px 16px", marginBottom: 16, border: "1px solid rgba(255,255,255,0.12)" }}>
              <p style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", color: "rgba(255,255,255,0.6)", margin: "0 0 10px" }}>Šiandien</p>
              {upcoming.length === 0 ? (
                <p style={{ fontSize: 13, color: "rgba(255,255,255,0.4)", margin: "0 0 10px" }}>
                  {todaySessions.length > 0 ? "Šiandienos treniruotės baigtos" : "Šiandien treniruočių nėra"}
                </p>
              ) : (
                <div style={{ display: "flex", flexDirection: "column", gap: 6, marginBottom: (adminBadges.bookings > 0 || adminBadges.packages > 0) ? 10 : 0 }}>
                  {upcoming.map(b => {
                    const c = clients.find(cl => cl.id === b.client_id);
                    const isOngoing = b.start_time <= nowTime && nowTime <= (b.end_time || b.start_time);
                    return (
                      <button key={b.id} onClick={() => c && (isOngoing ? openClientLive(c) : openClientSafe(c))} style={{
                        display: "flex", alignItems: "center", gap: 8, borderRadius: 12, padding: "9px 12px", cursor: c ? "pointer" : "default", fontFamily: "inherit", textAlign: "left", width: "100%",
                        background: isOngoing ? "rgba(127,255,176,0.14)" : "rgba(255,255,255,0.06)",
                        border: isOngoing ? "1px solid rgba(127,255,176,0.4)" : "1px solid transparent",
                      }}>
                        {isOngoing && <Dot color="#7FFFB0" size={8} style={{ animation:"livePulse 1.2s ease-in-out infinite", flexShrink:0 }} />}
                        <span style={{ fontSize: 11, fontWeight: 700, color: isOngoing ? "#7FFFB0" : "rgba(255,255,255,0.5)", flexShrink: 0, display: "flex", alignItems: "center", gap: 3 }}><Timer size={11} />{b.start_time}</span>
                        <span style={{ fontSize: 13, fontWeight: 600, color: "#fff", flex: 1 }}>{c?.name || "Klientas"}</span>
                        {isOngoing && <span style={{ fontSize:9, fontWeight:800, color:"#7FFFB0", background:"rgba(127,255,176,0.18)", borderRadius:20, padding:"2px 7px", letterSpacing:"0.04em", flexShrink:0 }}>VYKSTA</span>}
                        {c && <ChevronRight size={13} color="rgba(255,255,255,0.4)" />}
                      </button>
                    );
                  })}
                </div>
              )}
              {(adminBadges.bookings > 0 || adminBadges.packages > 0) && (
                <div style={{ display: "flex", gap: 8 }}>
                  {adminBadges.bookings > 0 && (
                    <button onClick={() => openAdminTab("bookings")} style={{ flex: 1, padding: "9px", borderRadius: 10, border: "1px solid rgba(255,215,0,0.3)", background: "rgba(255,215,0,0.08)", color: "#FFD700", fontSize: 12, fontWeight: 700, cursor: "pointer", fontFamily: "inherit", display: "flex", alignItems: "center", justifyContent: "center", gap: 5 }}>
                      <AlertTriangle size={12} />{adminBadges.bookings} rezervacij{adminBadges.bookings === 1 ? "a" : "os"} laukia
                    </button>
                  )}
                  {adminBadges.packages > 0 && (
                    <button onClick={() => openAdminTab("packages")} style={{ flex: 1, padding: "9px", borderRadius: 10, border: "1px solid rgba(255,215,0,0.3)", background: "rgba(255,215,0,0.08)", color: "#FFD700", fontSize: 12, fontWeight: 700, cursor: "pointer", fontFamily: "inherit", display: "flex", alignItems: "center", justifyContent: "center", gap: 5 }}>
                      <AlertTriangle size={12} />{adminBadges.packages} paket{adminBadges.packages === 1 ? "as" : "ai"} laukia
                    </button>
                  )}
                </div>
              )}
            </div>
            );
          })()}

          {calendarError && (
            <div style={{ background:"rgba(255,100,100,0.12)", border:"1px solid rgba(255,100,100,0.3)", borderRadius:12, padding:"10px 12px", marginBottom:12, display:"flex", alignItems:"flex-start", gap:8 }}>
              <AlertTriangle size={14} color="#FF8888" style={{flexShrink:0,marginTop:1}} />
              <p style={{fontSize:12,color:"#FFB3B3",margin:0}}>{calendarError}</p>
            </div>
          )}

          {notInCalendar.length > 0 && (
            <div style={{ background: "rgba(110,198,255,0.08)", border: "1px solid rgba(110,198,255,0.25)", borderRadius: 18, padding: "14px 16px", marginBottom: 16 }}>
              <p style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", color: "#6EC6FF", margin: "0 0 10px", display: "flex", alignItems: "center", gap: 6 }}>
                <Calendar size={11} />Nepridėta į kalendorių ({notInCalendar.length})
              </p>
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                {notInCalendar.slice(0, 5).map(b => {
                  const c = clients.find(cl => cl.id === b.client_id);
                  return (
                    <div key={b.id} style={{ display: "flex", alignItems: "center", gap: 8, background: "rgba(255,255,255,0.06)", borderRadius: 12, padding: "8px 10px" }}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <p style={{ fontSize: 12, fontWeight: 700, color: "#fff", margin: "0 0 1px" }}>{c?.name || "Klientas"}</p>
                        <p style={{ fontSize: 10, color: "rgba(255,255,255,0.5)", margin: 0 }}>{b.date?.slice(0,10)} · {b.start_time}–{b.end_time}</p>
                      </div>
                      <button onClick={() => handleAddToCalendar(b)} style={{ flexShrink: 0, background: "#2F8FE0", border: "none", borderRadius: 9, padding: "7px 11px", color: "#fff", fontSize: 11, fontWeight: 700, cursor: "pointer", fontFamily: "inherit", display: "flex", alignItems: "center", gap: 5 }}>
                        <Calendar size={11} />Pridėti
                      </button>
                    </div>
                  );
                })}
              </div>
              {notInCalendar.length > 5 && (
                <button onClick={() => openAdminTab("bookings")} style={{ width: "100%", marginTop: 8, padding: "8px", borderRadius: 10, border: "none", background: "transparent", color: "#6EC6FF", fontSize: 11, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}>
                  +{notInCalendar.length - 5} daugiau → Rezervacijos
                </button>
              )}
            </div>
          )}

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 16 }}>
            <DashStatCard icon={Users} c1="#6EC6FF" c2="#2F8FE0"
              value={clients.length} label="Klientai iš viso" />
            <DashStatCard icon={CheckCircle} c1="#7FE3A6" c2="#2FBE84"
              value={clients.filter(c => c.onboarding_done).length} label="Anketa baigta"
              pct={clients.length ? clients.filter(c => c.onboarding_done).length / clients.length * 100 : null} />
            <DashStatCard icon={Calendar} c1="#FFC15E" c2="#FF9F43"
              value={dashExtra.todayBookings} label="Šiandien rezervacijų" />
            <DashStatCard icon={Ticket} c1="#FF9FC7" c2="#E91E8C"
              value={dashExtra.weekPackages} label="Šią savaitę parduota paketų" />
          </div>

          <button onClick={goToNewClient} style={{ width: "100%", padding: "14px 0", marginBottom: 16, background: "linear-gradient(135deg,#6D1B3B,#AD1457)", color: "#fff", border: "none", borderRadius: 16, fontSize: 15, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}>
            + Naujas klientas
          </button>

          <p style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.12em", textTransform: "uppercase", color: "rgba(255,255,255,0.75)", marginBottom: 10 }}>Klientai</p>

          {loading ? (
            <p style={{ textAlign: "center", color: "rgba(255,255,255,0.5)", padding: "24px 0" }}>Kraunama...</p>
          ) : clients.length === 0 ? (
            <div style={{ background: "rgba(255,255,255,0.08)", borderRadius: 16, padding: "32px 20px", textAlign: "center", border: "2px dashed rgba(255,255,255,0.2)" }}>
              <p style={{ color: "rgba(255,255,255,0.5)", fontSize: 14 }}>Dar nėra klientų</p>
            </div>
          ) : (() => {
            const q = clientSearch.trim().toLowerCase();
            const filteredClients = q
              ? clients.filter(c => c.name?.toLowerCase().includes(q) || c.email?.toLowerCase().includes(q))
              : clients;
            return (
              <>
                {clients.length > 8 && (
                  <SearchInput value={clientSearch} onChange={v => { setClientSearch(v); setVisibleClients(8); }} placeholder="Ieškoti kliento pagal vardą..." />
                )}
                {filteredClients.length === 0 ? (
                  <p style={{ textAlign: "center", color: "rgba(255,255,255,0.4)", padding: "16px 0", fontSize: 13 }}>Klientų pagal paiešką nerasta</p>
                ) : (
                  <>
                    {filteredClients.slice(0, visibleClients).map(client => (
                      <ClientCard key={client.id} client={client} pkgInfo={pkgSummary[client.id]} onOpen={() => openClientSafe(client)} />
                    ))}
                    <ShowMoreButton remaining={filteredClients.length - visibleClients} onClick={() => setVisibleClients(v => v + 8)} />
                  </>
                )}
              </>
            );
          })()}
        </div>
      </div>

      {/* Tab bar apačioje — brolinis elementas scrollinamos zonos, ne jos viduje (iOS PWA fixed-in-scroll klaidai išvengti).
          Visada matoma (kad matytųsi perėjimo animacija tarp tabų) — pilno ekrano langai turi
          pakankamą paddingBottom, kad jų turinys nepasislėptų po juosta, o formų modalai (ExercisePicker
          ir pan.) turi aukštesnį z-index ir uždengia juostą, kol yra atidaryti. */}
      <div style={{
        position:"fixed", left:14, right:14, bottom:"max(14px, env(safe-area-inset-bottom))", zIndex:1000,
        maxWidth:452, margin:"0 auto",
        background:"rgba(24,7,17,0.82)", backdropFilter:"blur(20px)", WebkitBackdropFilter:"blur(20px)",
        border:"1px solid rgba(255,255,255,0.1)", borderRadius:26,
        display:"flex", alignItems:"center", justifyContent:"space-between", gap:3, padding:7,
        boxShadow:"0 10px 30px rgba(0,0,0,0.45), 0 1px 0 rgba(255,255,255,0.06) inset",
      }}>
        {[
          { id:"clients",  Icon:Users,     label:"Klientai",    badge:0,                    onClick: ()=>openAdminTab("clients") },
          { id:"bookings", Icon:Calendar,  label:"Rezervacijos", badge:adminBadges.bookings + notInCalendar.length, onClick: ()=>openAdminTab("bookings") },
          { id:"packages", Icon:Ticket,    label:"Paketai",      badge:adminBadges.packages, onClick: ()=>openAdminTab("packages") },
          { id:"stats",    Icon:BarChart,  label:"Statistika",   badge:0,                    onClick: ()=>openAdminTab("stats") },
          { id:"presets",  Icon:Clipboard, label:"Šablonai",     badge:0,                    onClick: ()=>openAdminTab("presets") },
        ].map(tab => {
          const isActive = activeAdminTab === tab.id;
          return (
            <button key={tab.id} onClick={tab.onClick}
              style={{
                display:"flex", alignItems:"center", justifyContent:"center", gap:isActive?6:0,
                background: isActive ? "linear-gradient(135deg,#AD1457,#6D1B3B)" : "transparent",
                border:"none", borderRadius:18, padding:isActive?"9px 13px":"9px 11px",
                cursor:"pointer", position:"relative", flexShrink:0,
                boxShadow: isActive ? "0 4px 14px rgba(173,20,87,0.45)" : "none",
                transition:"background 0.28s cubic-bezier(.34,1.56,.64,1), box-shadow 0.28s ease, padding 0.28s cubic-bezier(.34,1.56,.64,1)",
              }}>
              <div style={{ position:"relative", flexShrink:0 }}>
                <tab.Icon size={19} color={isActive ? "#fff" : "rgba(255,255,255,0.5)"} strokeWidth={isActive ? 2 : 1.6} style={{ transition:"color 0.2s ease" }} />
                {tab.badge > 0 && (
                  <div style={{ position:"absolute", top:-6, right:-7, background:"#FF4444", borderRadius:99, minWidth:14, height:14, display:"flex", alignItems:"center", justifyContent:"center", fontSize:8, fontWeight:800, color:"#fff", padding:"0 3px", boxShadow:"0 0 0 2px rgba(24,7,17,0.9)" }}>
                    {tab.badge}
                  </div>
                )}
              </div>
              <span style={{
                fontSize:12, fontWeight:700, color:"#fff", whiteSpace:"nowrap", overflow:"hidden",
                maxWidth: isActive ? 90 : 0, opacity: isActive ? 1 : 0,
                transition:"max-width 0.28s cubic-bezier(.34,1.56,.64,1), opacity 0.18s ease",
              }}>{tab.label}</span>
            </button>
          );
        })}
      </div>

      {/* Overlay langai */}
      {showBookings && <BookingAdmin onClose={() => setShowBookings(false)} onOpenClient={setOpenClient} />}
      {showStats    && <TrainerStats onClose={() => setShowStats(false)} onOpenClient={setOpenClient} />}
      {showPresets  && <WorkoutPresets onClose={() => setShowPresets(false)} />}
      {showPackages && <PackageAdmin onClose={() => setShowPackages(false)} onOpenClient={setOpenClient} />}

      {/* Kliento profilis — virš kitų overlay langų (700 > 500), kad atidarius klientą
          iš Rezervacijų/Paketų/Statistikos, uždarius jį grįžtume į TĄ PATĮ ekraną su
          išsaugotais filtrais/paieška, o ne į iš naujo užkrautą pradinę būseną. */}
      {openClient && <ClientDetail key={`${openClient.id}-${clientDetailNonce}`} client={openClient} onClose={() => openClientSafe(null)} autoOpenLiveTraining={pendingAutoOpenLiveTraining} />}
      <LiveSessionBanner info={liveSessionInfo} onClick={goToLiveSessionBanner} />
    </div>
  );

  // ── iPad/kompiuteris (≥1000px) — šoninis meniu + split-pane ────────────────
  // Sąmoningai NEBENDRINA JSX su mobilia šaka aukščiau (šiek tiek pasikartojimo),
  // kad mobili versija liktų visiškai izoliuota ir nepaliesta šio pakeitimo.
  const sidebarTabs = [
    { id:"clients",  Icon:Users,     label:"Klientai",     badge:0 },
    { id:"bookings", Icon:Calendar,  label:"Rezervacijos", badge:adminBadges.bookings + notInCalendar.length },
    { id:"packages", Icon:Ticket,    label:"Paketai",      badge:adminBadges.packages },
    { id:"stats",    Icon:BarChart,  label:"Statistika",   badge:0 },
    { id:"presets",  Icon:Clipboard, label:"Šablonai",     badge:0 },
  ];

  return (
    <div style={{ height:"100%", display:"flex", background:"linear-gradient(160deg,#2d0a1a 0%,#6D1B3B 40%,#AD1457 100%)", fontFamily:"-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif" }}>
      {/* Šoninis meniu */}
      <div style={{ width:232, flexShrink:0, display:"flex", flexDirection:"column", background:"rgba(0,0,0,0.25)", borderRight:"1px solid rgba(255,255,255,0.1)", padding:"20px 14px", boxSizing:"border-box" }}>
        <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:26, padding:"0 6px" }}>
          <img src="/logo.png" alt="Coach Vilma" style={{ width:34, height:34, objectFit:"contain", borderRadius:8 }} />
          <div>
            <h1 style={{ fontSize:15, fontWeight:700, color:"#fff", margin:0 }}>Vilma · Admin</h1>
            <p style={{ fontSize:10, color:"rgba(255,255,255,0.4)", margin:0 }}>Trenerės panelė</p>
          </div>
        </div>
        <div style={{ display:"flex", flexDirection:"column", gap:4, flex:1, minHeight:0, overflowY:"auto" }}>
          {sidebarTabs.map(tab => {
            const isActive = activeAdminTab === tab.id;
            return (
              <button key={tab.id} onClick={() => openAdminTab(tab.id)} style={{
                display:"flex", alignItems:"center", gap:12, padding:"11px 12px", borderRadius:14, border:"none",
                background: isActive ? "linear-gradient(135deg,#AD1457,#6D1B3B)" : "transparent",
                color:"#fff", cursor:"pointer", fontFamily:"inherit", textAlign:"left",
                boxShadow: isActive ? "0 4px 14px rgba(173,20,87,0.35)" : "none",
              }}>
                <tab.Icon size={18} color={isActive ? "#fff" : "rgba(255,255,255,0.6)"} strokeWidth={isActive ? 2 : 1.6} />
                <span style={{ fontSize:13, fontWeight:700, flex:1 }}>{tab.label}</span>
                {tab.badge > 0 && (
                  <span style={{ background:"#FF4444", borderRadius:99, minWidth:18, height:18, display:"flex", alignItems:"center", justifyContent:"center", fontSize:10, fontWeight:800, color:"#fff", padding:"0 5px" }}>{tab.badge}</span>
                )}
              </button>
            );
          })}
        </div>
        <div style={{ display:"flex", flexDirection:"column", gap:8, paddingTop:14, borderTop:"1px solid rgba(255,255,255,0.1)" }}>
          <button onClick={goToNewClient} style={{ width:"100%", padding:"10px", borderRadius:12, background:"linear-gradient(135deg,#6D1B3B,#AD1457)", border:"none", color:"#fff", fontSize:12, fontWeight:700, cursor:"pointer", fontFamily:"inherit" }}>+ Naujas klientas</button>
          <div style={{ display:"flex", gap:6 }}>
            <button onClick={handleRefresh} disabled={refreshing} aria-label="Atnaujinti" style={{ flex:"0 0 auto", width:34, height:34, background:"rgba(255,255,255,0.08)", border:"1px solid rgba(255,255,255,0.15)", borderRadius:10, color:"rgba(255,255,255,0.85)", cursor:refreshing?"default":"pointer", display:"flex", alignItems:"center", justifyContent:"center" }}>
              <Refresh size={14} style={{ animation: refreshing?"spin 0.7s linear infinite":"none" }} />
            </button>
            <button onClick={handleLogoutClick} style={{ flex:1, background:"rgba(255,255,255,0.08)", border:"1px solid rgba(255,255,255,0.15)", borderRadius:10, padding:"8px", color:"rgba(255,255,255,0.7)", fontSize:11, fontWeight:600, cursor:"pointer", fontFamily:"inherit" }}>Atsijungti</button>
          </div>
        </div>
      </div>

      {/* Turinio sritis */}
      <div style={{ flex:1, minHeight:0, position:"relative", display:"flex", overflow:"hidden" }}>
        {activeAdminTab === "clients" ? (
          <>
            {/* Klientų sąrašas — kairėje */}
            <div style={{ width:380, flexShrink:0, borderRight:"1px solid rgba(255,255,255,0.1)", overflowY:"auto", WebkitOverflowScrolling:"touch", padding:20, boxSizing:"border-box" }}>
              <PushPermissionPrompt userId={user.id} />

              {(() => {
                const todayBdays = clients.filter(c => daysUntilBirthday(c.dob) === 0);
                const weekBdays = clients
                  .map(c => ({ c, d: daysUntilBirthday(c.dob) }))
                  .filter(x => x.d != null && x.d >= 1 && x.d <= 7)
                  .sort((a, b) => a.d - b.d);
                if (!todayBdays.length && !weekBdays.length) return null;
                return (
                  <div style={{ background: "linear-gradient(135deg,rgba(255,215,0,0.12),rgba(255,110,180,0.08))", border: "1px solid rgba(255,215,0,0.3)", borderRadius: 18, padding: "14px 16px", marginBottom: 16 }}>
                    <p style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", color: "#FFD700", margin: "0 0 10px", display: "flex", alignItems: "center", gap: 6 }}>
                      <Cake size={12} />Gimtadieniai
                    </p>
                    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                      {todayBdays.map(c => (
                        <button key={c.id} onClick={() => openClientSafe(c)} style={{ display: "flex", alignItems: "center", gap: 8, background: "linear-gradient(135deg,rgba(255,215,0,0.2),rgba(255,110,180,0.16))", border: "1px solid rgba(255,215,0,0.45)", borderRadius: 12, padding: "9px 12px", cursor: "pointer", fontFamily: "inherit", textAlign: "left", width: "100%", animation: "bdayCardGlow 2s ease-in-out infinite" }}>
                          <Cake size={14} color="#FFD700" style={{ flexShrink: 0 }} />
                          <span style={{ fontSize: 13, fontWeight: 700, color: "#fff", flex: 1 }}>{c.name}</span>
                          <span style={{ fontSize: 10, fontWeight: 800, color: "#FFD700", background: "rgba(255,215,0,0.22)", borderRadius: 20, padding: "2px 8px", flexShrink: 0 }}>ŠIANDIEN</span>
                          <ChevronRight size={13} color="rgba(255,255,255,0.4)" />
                        </button>
                      ))}
                      {weekBdays.map(({ c, d }) => (
                        <button key={c.id} onClick={() => openClientSafe(c)} style={{ display: "flex", alignItems: "center", gap: 8, background: "rgba(255,255,255,0.06)", border: "1px solid transparent", borderRadius: 12, padding: "9px 12px", cursor: "pointer", fontFamily: "inherit", textAlign: "left", width: "100%" }}>
                          <Cake size={13} color="rgba(255,215,0,0.7)" style={{ flexShrink: 0 }} />
                          <span style={{ fontSize: 13, fontWeight: 600, color: "#fff", flex: 1 }}>{c.name}</span>
                          <span style={{ fontSize: 11, color: "rgba(255,255,255,0.5)", flexShrink: 0 }}>{d === 1 ? "rytoj" : `už ${d} d.`}</span>
                          <ChevronRight size={13} color="rgba(255,255,255,0.4)" />
                        </button>
                      ))}
                    </div>
                  </div>
                );
              })()}

              {(() => {
                const upcoming = todaySessions.filter(b => (b.end_time || b.start_time) >= nowTime);
                const showCard = upcoming.length > 0 || adminBadges.bookings > 0 || adminBadges.packages > 0;
                if (!showCard) return null;
                return (
                <div style={{ background: "rgba(255,255,255,0.06)", borderRadius: 18, padding: "14px 16px", marginBottom: 16, border: "1px solid rgba(255,255,255,0.12)" }}>
                  <p style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", color: "rgba(255,255,255,0.6)", margin: "0 0 10px" }}>Šiandien</p>
                  {upcoming.length === 0 ? (
                    <p style={{ fontSize: 13, color: "rgba(255,255,255,0.4)", margin: "0 0 10px" }}>
                      {todaySessions.length > 0 ? "Šiandienos treniruotės baigtos" : "Šiandien treniruočių nėra"}
                    </p>
                  ) : (
                    <div style={{ display: "flex", flexDirection: "column", gap: 6, marginBottom: (adminBadges.bookings > 0 || adminBadges.packages > 0) ? 10 : 0 }}>
                      {upcoming.map(b => {
                        const c = clients.find(cl => cl.id === b.client_id);
                        const isOngoing = b.start_time <= nowTime && nowTime <= (b.end_time || b.start_time);
                        return (
                          <button key={b.id} onClick={() => c && (isOngoing ? openClientLive(c) : openClientSafe(c))} style={{
                            display: "flex", alignItems: "center", gap: 8, borderRadius: 12, padding: "9px 12px", cursor: c ? "pointer" : "default", fontFamily: "inherit", textAlign: "left", width: "100%",
                            background: isOngoing ? "rgba(127,255,176,0.14)" : "rgba(255,255,255,0.06)",
                            border: isOngoing ? "1px solid rgba(127,255,176,0.4)" : "1px solid transparent",
                          }}>
                            {isOngoing && <Dot color="#7FFFB0" size={8} style={{ animation:"livePulse 1.2s ease-in-out infinite", flexShrink:0 }} />}
                            <span style={{ fontSize: 11, fontWeight: 700, color: isOngoing ? "#7FFFB0" : "rgba(255,255,255,0.5)", flexShrink: 0, display: "flex", alignItems: "center", gap: 3 }}><Timer size={11} />{b.start_time}</span>
                            <span style={{ fontSize: 13, fontWeight: 600, color: "#fff", flex: 1 }}>{c?.name || "Klientas"}</span>
                            {isOngoing && <span style={{ fontSize:9, fontWeight:800, color:"#7FFFB0", background:"rgba(127,255,176,0.18)", borderRadius:20, padding:"2px 7px", letterSpacing:"0.04em", flexShrink:0 }}>VYKSTA</span>}
                            {c && <ChevronRight size={13} color="rgba(255,255,255,0.4)" />}
                          </button>
                        );
                      })}
                    </div>
                  )}
                  {(adminBadges.bookings > 0 || adminBadges.packages > 0) && (
                    <div style={{ display: "flex", gap: 8 }}>
                      {adminBadges.bookings > 0 && (
                        <button onClick={() => openAdminTab("bookings")} style={{ flex: 1, padding: "9px", borderRadius: 10, border: "1px solid rgba(255,215,0,0.3)", background: "rgba(255,215,0,0.08)", color: "#FFD700", fontSize: 12, fontWeight: 700, cursor: "pointer", fontFamily: "inherit", display: "flex", alignItems: "center", justifyContent: "center", gap: 5 }}>
                          <AlertTriangle size={12} />{adminBadges.bookings} rezervacij{adminBadges.bookings === 1 ? "a" : "os"} laukia
                        </button>
                      )}
                      {adminBadges.packages > 0 && (
                        <button onClick={() => openAdminTab("packages")} style={{ flex: 1, padding: "9px", borderRadius: 10, border: "1px solid rgba(255,215,0,0.3)", background: "rgba(255,215,0,0.08)", color: "#FFD700", fontSize: 12, fontWeight: 700, cursor: "pointer", fontFamily: "inherit", display: "flex", alignItems: "center", justifyContent: "center", gap: 5 }}>
                          <AlertTriangle size={12} />{adminBadges.packages} paket{adminBadges.packages === 1 ? "as" : "ai"} laukia
                        </button>
                      )}
                    </div>
                  )}
                </div>
                );
              })()}

              {calendarError && (
                <div style={{ background:"rgba(255,100,100,0.12)", border:"1px solid rgba(255,100,100,0.3)", borderRadius:12, padding:"10px 12px", marginBottom:12, display:"flex", alignItems:"flex-start", gap:8 }}>
                  <AlertTriangle size={14} color="#FF8888" style={{flexShrink:0,marginTop:1}} />
                  <p style={{fontSize:12,color:"#FFB3B3",margin:0}}>{calendarError}</p>
                </div>
              )}

              {notInCalendar.length > 0 && (
                <div style={{ background: "rgba(110,198,255,0.08)", border: "1px solid rgba(110,198,255,0.25)", borderRadius: 18, padding: "14px 16px", marginBottom: 16 }}>
                  <p style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", color: "#6EC6FF", margin: "0 0 10px", display: "flex", alignItems: "center", gap: 6 }}>
                    <Calendar size={11} />Nepridėta į kalendorių ({notInCalendar.length})
                  </p>
                  <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                    {notInCalendar.slice(0, 5).map(b => {
                      const c = clients.find(cl => cl.id === b.client_id);
                      return (
                        <div key={b.id} style={{ display: "flex", alignItems: "center", gap: 8, background: "rgba(255,255,255,0.06)", borderRadius: 12, padding: "8px 10px" }}>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <p style={{ fontSize: 12, fontWeight: 700, color: "#fff", margin: "0 0 1px" }}>{c?.name || "Klientas"}</p>
                            <p style={{ fontSize: 10, color: "rgba(255,255,255,0.5)", margin: 0 }}>{b.date?.slice(0,10)} · {b.start_time}–{b.end_time}</p>
                          </div>
                          <button onClick={() => handleAddToCalendar(b)} style={{ flexShrink: 0, background: "#2F8FE0", border: "none", borderRadius: 9, padding: "7px 11px", color: "#fff", fontSize: 11, fontWeight: 700, cursor: "pointer", fontFamily: "inherit", display: "flex", alignItems: "center", gap: 5 }}>
                            <Calendar size={11} />Pridėti
                          </button>
                        </div>
                      );
                    })}
                  </div>
                  {notInCalendar.length > 5 && (
                    <button onClick={() => openAdminTab("bookings")} style={{ width: "100%", marginTop: 8, padding: "8px", borderRadius: 10, border: "none", background: "transparent", color: "#6EC6FF", fontSize: 11, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}>
                      +{notInCalendar.length - 5} daugiau → Rezervacijos
                    </button>
                  )}
                </div>
              )}

              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 16 }}>
                <DashStatCard icon={Users} c1="#6EC6FF" c2="#2F8FE0"
                  value={clients.length} label="Klientai iš viso" />
                <DashStatCard icon={CheckCircle} c1="#7FE3A6" c2="#2FBE84"
                  value={clients.filter(c => c.onboarding_done).length} label="Anketa baigta"
                  pct={clients.length ? clients.filter(c => c.onboarding_done).length / clients.length * 100 : null} />
                <DashStatCard icon={Calendar} c1="#FFC15E" c2="#FF9F43"
                  value={dashExtra.todayBookings} label="Šiandien rezervacijų" />
                <DashStatCard icon={Ticket} c1="#FF9FC7" c2="#E91E8C"
                  value={dashExtra.weekPackages} label="Šią savaitę parduota paketų" />
              </div>

              <p style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.12em", textTransform: "uppercase", color: "rgba(255,255,255,0.75)", marginBottom: 10 }}>Klientai</p>

              {loading ? (
                <p style={{ textAlign: "center", color: "rgba(255,255,255,0.5)", padding: "24px 0" }}>Kraunama...</p>
              ) : clients.length === 0 ? (
                <div style={{ background: "rgba(255,255,255,0.08)", borderRadius: 16, padding: "32px 20px", textAlign: "center", border: "2px dashed rgba(255,255,255,0.2)" }}>
                  <p style={{ color: "rgba(255,255,255,0.5)", fontSize: 14 }}>Dar nėra klientų</p>
                </div>
              ) : (() => {
                const q = clientSearch.trim().toLowerCase();
                const filteredClients = q
                  ? clients.filter(c => c.name?.toLowerCase().includes(q) || c.email?.toLowerCase().includes(q))
                  : clients;
                return (
                  <>
                    {clients.length > 8 && (
                      <SearchInput value={clientSearch} onChange={v => { setClientSearch(v); setVisibleClients(8); }} placeholder="Ieškoti kliento pagal vardą..." />
                    )}
                    {filteredClients.length === 0 ? (
                      <p style={{ textAlign: "center", color: "rgba(255,255,255,0.4)", padding: "16px 0", fontSize: 13 }}>Klientų pagal paiešką nerasta</p>
                    ) : (
                      <>
                        {filteredClients.slice(0, visibleClients).map(client => (
                          <ClientCard key={client.id} client={client} pkgInfo={pkgSummary[client.id]} onOpen={() => openClientSafe(client)} />
                        ))}
                        <ShowMoreButton remaining={filteredClients.length - visibleClients} onClick={() => setVisibleClients(v => v + 8)} />
                      </>
                    )}
                  </>
                );
              })()}
            </div>

            {/* Kliento info — dešinėje */}
            <div style={{ flex:1, minHeight:0, position:"relative", overflowY:"auto", WebkitOverflowScrolling:"touch" }}>
              {openClient ? (
                <ClientDetail key={`${openClient.id}-${clientDetailNonce}`} client={openClient} onClose={() => openClientSafe(null)} autoOpenLiveTraining={pendingAutoOpenLiveTraining} embedded />
              ) : (
                <div style={{ height:"100%", display:"flex", alignItems:"center", justifyContent:"center", flexDirection:"column", gap:10, color:"rgba(255,255,255,0.3)" }}>
                  <Users size={48} />
                  <p style={{ fontSize:14 }}>Pasirinkite klientą iš sąrašo kairėje</p>
                </div>
              )}
            </div>
          </>
        ) : (
          <div style={{ flex:1, minHeight:0, position:"relative" }}>
            {showBookings && <BookingAdmin onClose={() => setShowBookings(false)} onOpenClient={setOpenClient} />}
            {showStats    && <TrainerStats onClose={() => setShowStats(false)} onOpenClient={setOpenClient} />}
            {showPresets  && <WorkoutPresets onClose={() => setShowPresets(false)} />}
            {showPackages && <PackageAdmin onClose={() => setShowPackages(false)} onOpenClient={setOpenClient} />}
            {openClient && <ClientDetail key={`${openClient.id}-${clientDetailNonce}`} client={openClient} onClose={() => openClientSafe(null)} autoOpenLiveTraining={pendingAutoOpenLiveTraining} embedded />}
          </div>
        )}
      </div>
      <LiveSessionBanner info={liveSessionInfo} onClick={goToLiveSessionBanner} />
    </div>
  );
}
